import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion, Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, ButtonGroup, Dropdown, DropdownButton } from 'react-bootstrap';
import Sortable from 'sortablejs';
import { AlignHorizontalJustifyCenter, BetweenHorizontalStart, BetweenVerticalStart, Check, SquareCheck, CircleChevronDown, TextAlignJustify, TextCursorInput, TextInitial, AlignStartVertical, AlignEndVertical, ArrowDown, ArrowLeft, ArrowRight, ArrowUp, CheckSquare, Copy, Eye, EyeOff, SquarePen, FileText, GripVertical, Hash, Heading, Image, Mail, Minus, PenLine, Phone, Plus, Save, Settings, Square, SquareDashed, SquareDashedTopSolid, Table, Trash2, Type, X } from 'lucide-react';
import SignatureField from '../components/SignatureField';
import Navbar from '../Navbar';
import PageTitle from '../components/PageTitle';
import { API_ENDPOINTS } from '../config/api';
import RichTextEditor from '../components/RichTextEditor';
import NavigationWarningModal from '../components/NavigationWarningModal';

/** Column/row insert affordances in the form builder table editor */
const TABLE_INSERT_ACCENT = 'var(--blue)';

/** Viewport top-left of the wrapper's padding edge — matches coords for absolutely positioned descendants. */
function wrapPaddingEdgeOrigin(wrap) {
  if (!wrap) return { left: 0, top: 0 };
  const r = wrap.getBoundingClientRect();
  const st = window.getComputedStyle(wrap);
  const bl = parseFloat(st.borderLeftWidth) || 0;
  const bt = parseFloat(st.borderTopWidth) || 0;
  return { left: r.left + bl, top: r.top + bt };
}

/** Same as wrapPaddingEdgeOrigin — old name kept for mixed HMR bundles that still call it. */
function wrapContentBoxTopLeft(wrap) {
  return wrapPaddingEdgeOrigin(wrap);
}

const FLOAT_DEL_HIDE_MS = 240;

function FormBuilderEditableTable({ field, previewMode, addTableColumn, addTableRow, deleteTableColumn, deleteTableRow, updateTableCell }) {
  const wrapRef = useRef(null);
  const headerDelHideTimerRef = useRef(null);
  const bodyDelHideTimerRef = useRef(null);
  const [colGutter, setColGutter] = useState(null); // { insertAfterCol, lineLeft }
  const [rowGutter, setRowGutter] = useState(null); // { mode: 'beforeRow'|'afterLast', rowIndex?, lineTop }
  const [headerDelCol, setHeaderDelCol] = useState(null);
  const [bodyDelRow, setBodyDelRow] = useState(null);

  const cancelHeaderDelHide = () => {
    if (headerDelHideTimerRef.current != null) {
      clearTimeout(headerDelHideTimerRef.current);
      headerDelHideTimerRef.current = null;
    }
  };
  const scheduleHeaderDelHide = () => {
    cancelHeaderDelHide();
    headerDelHideTimerRef.current = window.setTimeout(() => {
      headerDelHideTimerRef.current = null;
      setHeaderDelCol(null);
    }, FLOAT_DEL_HIDE_MS);
  };
  const cancelBodyDelHide = () => {
    if (bodyDelHideTimerRef.current != null) {
      clearTimeout(bodyDelHideTimerRef.current);
      bodyDelHideTimerRef.current = null;
    }
  };
  const scheduleBodyDelHide = () => {
    cancelBodyDelHide();
    bodyDelHideTimerRef.current = window.setTimeout(() => {
      bodyDelHideTimerRef.current = null;
      setBodyDelRow(null);
    }, FLOAT_DEL_HIDE_MS);
  };

  useEffect(() => () => {
    cancelHeaderDelHide();
    cancelBodyDelHide();
  }, []);

  const updateColGuide = useCallback((insertAfterCol) => {
    setRowGutter(null);
    requestAnimationFrame(() => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const firstRow = wrap.querySelector('tbody tr');
      if (!firstRow) return;
      const cells = firstRow.querySelectorAll('th, td');
      const origin = wrapPaddingEdgeOrigin(wrap);
      const headerTop = firstRow.getBoundingClientRect().top - origin.top;
      let lineLeft;
      if (insertAfterCol < 0) {
        const cell = cells[0];
        if (!cell) return;
        lineLeft = cell.getBoundingClientRect().left - origin.left;
      } else {
        const cell = cells[insertAfterCol];
        if (!cell) return;
        lineLeft = cell.getBoundingClientRect().right - origin.left;
      }
      setColGutter({ insertAfterCol, lineLeft, lineTop: headerTop });
    });
  }, []);

  const updateRowGuide = useCallback((mode, rowIndex) => {
    setColGutter(null);
    requestAnimationFrame(() => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const rows = wrap.querySelectorAll('tbody tr');
      const origin = wrapPaddingEdgeOrigin(wrap);
      const tableEl = wrap.querySelector('table');
      const trect = tableEl?.getBoundingClientRect();
      const rowLineLeft = trect ? trect.left - origin.left : 0;
      const rowLineWidth = trect ? trect.width : wrap.clientWidth;
      if (mode === 'afterLast') {
        const last = rows[rows.length - 1];
        if (!last) return;
        const lineTop = last.getBoundingClientRect().bottom - origin.top;
        setRowGutter({
          mode: 'afterLast',
          lineTop,
          rowLineLeft,
          rowLineWidth,
        });
      } else {
        const row = rows[rowIndex];
        if (!row) return;
        const lineTop = row.getBoundingClientRect().top - origin.top;
        setRowGutter({
          mode: 'beforeRow',
          rowIndex,
          lineTop,
          rowLineLeft,
          rowLineWidth,
        });
      }
    });
  }, []);

  const handleWrapMouseLeave = (e) => {
    const next = e.relatedTarget;
    if (next instanceof Node && wrapRef.current?.contains(next)) return;
    cancelHeaderDelHide();
    cancelBodyDelHide();
    setColGutter(null);
    setRowGutter(null);
    setHeaderDelCol(null);
    setBodyDelRow(null);
  };

  const insertColAtGuide = () => {
    if (!colGutter) return;
    if (colGutter.insertAfterCol < 0) addTableColumn(field.id, 0, 'left');
    else addTableColumn(field.id, colGutter.insertAfterCol, 'right');
  };

  const insertRowAtGuide = () => {
    if (!rowGutter) return;
    if (rowGutter.mode === 'afterLast') addTableRow(field.id, field.tableData.length - 1, 'below');
    else addTableRow(field.id, rowGutter.rowIndex, 'above');
  };

  const plusBtnStyle = {
    width: 22,
    height: 22,
    borderRadius: '50%',
    border: `2px solid ${TABLE_INSERT_ACCENT}`,
    background: 'var(--card-bg)',
    color: TABLE_INSERT_ACCENT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxShadow: 'var(--shadow-custom)',
    cursor: 'pointer',
    zIndex: 4,
  };

  if (!Array.isArray(field.tableData)) return null;

  if (previewMode) {
    return (
      <div className="table-responsive ">
        <table className="table  align-middle custom-border rounded-3 border-1 overflow-hidden mb-0">
          <tbody>
            {field.tableData.map((row, r) => (
              <tr key={`row-${r}`}>
                {row.map((cell, c) => {
                  const isTopHeaderCell = r === 0;
                  const CellTag = r === 0 ? 'th' : 'td';
                  const cellStyle = {
                    position: 'relative',
                    minWidth: 0,
                    ...(isTopHeaderCell ? { backgroundColor: 'var(--page-bg)' } : {}),
                  };
                  return (
                    <CellTag key={cell.id} style={cellStyle}>
                      <div style={{ minHeight: 24, whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{cell.content}</div>
                    </CellTag>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div
      ref={wrapRef}
      className="table-responsive onboarding-fb-editable-table-wrap mb-3"
      style={{ position: 'relative', paddingTop: 36, paddingLeft: '2.5rem', paddingRight: '1rem', overflowX: 'visible', overflowY: 'visible' }}
      onMouseLeave={handleWrapMouseLeave}
    >
      <table className="table table-builder-fit align-middle custom-border rounded-3 border-1" style={{ overflow: 'visible' }}>
        <tbody>
          {field.tableData.map((row, r) => (
            <tr
              key={`row-${r}`}
              onMouseEnter={() => {
                if (r === 0) {
                  cancelBodyDelHide();
                  setBodyDelRow(null);
                  return;
                }
                cancelBodyDelHide();
                setBodyDelRow(r);
              }}
              onMouseLeave={() => scheduleBodyDelHide()}
            >
              {row.map((cell, c) => {
                const isTopHeaderCell = r === 0;
                const CellTag = r === 0 ? 'th' : 'td';
                const cellStyle = {
                  position: 'relative',
                  minWidth: 0,
                  ...(isTopHeaderCell ? { backgroundColor: 'var(--page-bg)' } : {}),
                };
                const isLastRow = r === field.tableData.length - 1;
                return (
                  <CellTag
                    key={cell.id}
                    style={cellStyle}
                    onMouseEnter={() => {
                      if (r === 0) {
                        cancelHeaderDelHide();
                        setHeaderDelCol(c);
                      }
                    }}
                    onMouseLeave={() => {
                      if (r === 0) scheduleHeaderDelHide();
                    }}
                  >
                    <div
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => updateTableCell(field.id, r, c, e.currentTarget.textContent || '')}
                      className="outline-none"
                      style={{ minHeight: 24, whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}
                    >
                      {cell.content}
                    </div>
                    {r === 0 && c === 0 && (
                      <div
                        role="presentation"
                        title="Add column"
                        onMouseEnter={() => updateColGuide(-1)}
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: -4,
                          width: 12,
                          bottom: -4,
                          zIndex: 3,
                          cursor: 'pointer',
                        }}
                      />
                    )}
                    {r === 0 && (
                      <div
                        role="presentation"
                        title="Add column"
                        onMouseEnter={() => updateColGuide(c)}
                        style={{
                          position: 'absolute',
                          right: 0,
                          top: -4,
                          width: 12,
                          bottom: -4,
                          zIndex: 3,
                          cursor: 'pointer',
                        }}
                      />
                    )}
                    {r === 0 && headerDelCol === c && (
                      <Button
                        type="button"
                        size="sm"
                        variant="light"
                        title="Delete column"
                        className="p-1 d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          position: 'absolute',
                          left: '50%',
                          bottom: '100%',
                          transform: 'translateX(-50%)',
                          marginBottom: 6,
                          width: 30,
                          height: 30,
                          boxShadow: 'var(--shadow-custom)',
                          zIndex: 12,
                          pointerEvents: 'auto',
                        }}
                        onMouseEnter={() => {
                          cancelHeaderDelHide();
                          setHeaderDelCol(c);
                        }}
                        onMouseLeave={() => scheduleHeaderDelHide()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          cancelHeaderDelHide();
                          deleteTableColumn(field.id, c);
                        }}
                      >
                        <Trash2 className="lucide-icon-sm" />
                      </Button>
                    )}
                    {r > 0 && c === 0 && (
                      <div
                        role="presentation"
                        title="Add row"
                        onMouseEnter={() => updateRowGuide('beforeRow', r)}
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          top: 0,
                          height: 16,
                          zIndex: 3,
                          cursor: 'pointer',
                        }}
                      />
                    )}
                    {r > 0 && isLastRow && c === 0 && (
                      <div
                        role="presentation"
                        title="Add row"
                        onMouseEnter={() => updateRowGuide('afterLast')}
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: 0,
                          height: 16,
                          zIndex: 3,
                          cursor: 'pointer',
                        }}
                      />
                    )}
                    {r > 0 && bodyDelRow === r && c === 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="light"
                        title="Delete row"
                        className="p-1 d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: '50%',
                          transform: 'translate(calc(-100% - 8px), -50%)',
                          width: 30,
                          height: 30,
                          boxShadow: 'var(--shadow-custom)',
                          zIndex: 12,
                        }}
                        onMouseEnter={() => {
                          cancelBodyDelHide();
                          setBodyDelRow(r);
                        }}
                        onMouseLeave={() => scheduleBodyDelHide()}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          cancelBodyDelHide();
                          deleteTableRow(field.id, r);
                        }}
                      >
                        <Trash2 className="lucide-icon-sm" />
                      </Button>
                    )}
                  </CellTag>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {colGutter && (
        <>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: colGutter.lineLeft - 1,
              top: colGutter.lineTop,
              bottom: 0,
              width: 2,
              background: TABLE_INSERT_ACCENT,
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />
          <button
            type="button"
            title="Add column"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              insertColAtGuide();
            }}
            className="d-flex align-items-center justify-content-center"
            style={{
              ...plusBtnStyle,
              position: 'absolute',
              left: colGutter.lineLeft - 11,
              top: colGutter.lineTop - 11,
              zIndex: 11,
            }}
          >
            <Plus size={13} strokeWidth={2.5} />
          </button>
        </>
      )}
      {rowGutter && (
        <>
          <div
            aria-hidden
            style={{
              position: 'absolute',
              left: rowGutter.rowLineLeft,
              width: rowGutter.rowLineWidth,
              top: rowGutter.lineTop - 1,
              height: 2,
              background: TABLE_INSERT_ACCENT,
              pointerEvents: 'none',
              zIndex: 10,
            }}
          />
          <button
            type="button"
            title="Add row"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              insertRowAtGuide();
            }}
            className="d-flex align-items-center justify-content-center"
            style={{
              ...plusBtnStyle,
              position: 'absolute',
              left: rowGutter.rowLineLeft - 11,
              top: rowGutter.lineTop - 11,
              zIndex: 11,
            }}
          >
            <Plus size={13} strokeWidth={2.5} />
          </button>
        </>
      )}
    </div>
  );
}

const FIELD_TYPES = [
  { id: 'header', name: 'Header', icon: Heading },
  { id: 'textinfo', name: 'Text Info', icon: TextAlignJustify },
  { id: 'text', name: 'Text', icon: Type },
  { id: 'textarea', name: 'Text Area', icon: TextCursorInput },
  { id: 'rich_textarea', name: 'Rich Text', icon: TextInitial },
  { id: 'email', name: 'Email', icon: Mail },
  { id: 'phone', name: 'Phone', icon: Phone },
  { id: 'ssn', name: 'SSN', icon: EyeOff },
  { id: 'number', name: 'Number', icon: Hash },
  { id: 'dropdown', name: 'Dropdown', icon: CircleChevronDown },
  { id: 'checkbox', name: 'Checkbox', icon: SquareCheck },
  { id: 'yes_no', name: 'Yes / No', icon: CheckSquare },
  { id: 'true_false', name: 'True/False', icon: Check },
  { id: 'signature', name: 'Signature', icon: PenLine },
  { id: 'table', name: 'Table', icon: Table },
  { id: 'divider', name: 'Divider', icon: Minus },
  { id: 'spacer', name: 'Spacer', icon: BetweenHorizontalStart },
  { id: 'header_block', name: 'Logo', icon: Image }
];

const PREFORMED_FIELDS = [
  { section: 'Name', items: [{ label: 'First Name', type: 'text' }, { label: 'Middle Name', type: 'text' }, { label: 'Last Name', type: 'text' }] },
  { section: 'Common', items: [{ label: 'Email', type: 'email' }, { label: 'Phone Number', type: 'phone' }, { label: 'SSN', type: 'ssn' }, { label: 'Date of Birth', type: 'date' }, { label: 'Employee ID', type: 'text' }] }
];

const TEMPLATE_BLOCKS = [
  {
    id: 'name_block',
    name: 'Name Block',
    sectionTitle: 'Name',
    rows: [[
      { label: 'First Name', type: 'text', flexBasis: 1 },
      { label: 'Middle Name', type: 'text', flexBasis: 1 },
      { label: 'Last Name', type: 'text', flexBasis: 1 }
    ]]
  },
  {
    id: 'address_block',
    name: 'Address Block',
    sectionTitle: 'Address',
    rows: [
      [
        { label: 'Address', type: 'text', flexBasis: 2 },
        { label: 'Apt/Suite', type: 'text', flexBasis: 1 },
        { label: 'City', type: 'text', flexBasis: 1 }
      ],
      [
        { label: 'State', type: 'text', flexBasis: 1 },
        { label: 'ZIP', type: 'text', flexBasis: 1 },
        { label: 'Country', type: 'dropdown', flexBasis: 1, options: [{ id: 'us', value: 'us', label: 'United States' }, { id: 'ca', value: 'ca', label: 'Canada' }] }
      ]
    ]
  },
  {
    id: 'citizenship_block',
    name: 'Citizenship Block',
    sectionTitle: 'Citizenship Information',
    rows: [
      [
        { label: 'Country of Citizenship', type: 'text', flexBasis: 1.5 },
        { label: 'Country of Birth', type: 'text', flexBasis: 1.5 }
      ],
      [
        { label: 'Dual Citizenship', type: 'yes_no', flexBasis: 1 },
        { label: 'Primary Nationality', type: 'text', flexBasis: 1.5 }
      ],
      [
        { label: 'Visa/Immigration Status', type: 'text', flexBasis: 2 }
      ]
    ]
  },
  {
    id: 'employment_history_block',
    name: 'Employment History Block',
    sectionTitle: 'Employment History',
    sectionDescription: 'Capture prior work experience similar to a resume.',
    rows: [
      [
        { label: 'Employer / Company Name', type: 'text', flexBasis: 2 },
        { label: 'Job Title', type: 'text', flexBasis: 1.5 }
      ],
      [
        { label: 'Start Date', type: 'date', flexBasis: 1 },
        { label: 'End Date', type: 'date', flexBasis: 1 },
        { label: 'Current Role', type: 'yes_no', flexBasis: 1 }
      ],
      [
        { label: 'City', type: 'text', flexBasis: 1 },
        { label: 'State/Province', type: 'text', flexBasis: 1 },
        { label: 'Country', type: 'text', flexBasis: 1 }
      ],
      [
        { label: 'Key Responsibilities', type: 'rich_textarea', flexBasis: 2 }],
        [{ label: 'Reason for Leaving', type: 'textarea', flexBasis: 2 }
      ]
    ]
  },
  {
    id: 'emergency_contact',
    name: 'Emergency Contact Block',
    sectionTitle: 'Emergency Contact',
    fields: [
      { label: 'Contact Full Name', type: 'text', flexBasis: 1.5 },
      { label: 'Relationship', type: 'text', flexBasis: 1 },
      { label: 'Phone Number', type: 'phone', flexBasis: 1 }
    ]
  },
  {
    id: 'employment_profile',
    name: 'Employment Profile Block',
    sectionTitle: 'Employment Profile',
    rows: [
      [
        { label: 'Employee ID', type: 'text', flexBasis: 1 },
        { label: 'Manager Name', type: 'text', flexBasis: 1.5 },
        { label: 'Department', type: 'text', flexBasis: 1 }
      ],
      [{ label: 'Start Date', type: 'date', flexBasis: 1 },
      { label: 'Work Email', type: 'email', flexBasis: 1.5 }]
    ]
  },
  {
    id: 'education_history_block',
    name: 'Education History Block',
    sectionTitle: 'Education History',
    sectionDescription: 'Capture the most recent education details.',
    rows: [
      [
        { label: 'Institution Name', type: 'text', flexBasis: 2 },
        { label: 'Degree / Program', type: 'text', flexBasis: 1.5 }
      ],
      [
        { label: 'Field of Study', type: 'text', flexBasis: 1.5 },
        { label: 'GPA (optional)', type: 'text', flexBasis: 1 }
      ],
      [
        { label: 'Start Date', type: 'date', flexBasis: 1 },
        { label: 'Graduation Date', type: 'date', flexBasis: 1 },
        { label: 'Graduated?', type: 'yes_no', flexBasis: 1 }
      ]
    ]
  },
  {
    id: 'criminal_history_block',
    name: 'Criminal History Block',
    sectionTitle: 'Criminal History',
    sectionDescription: 'Collect disclosure details when applicable.',
    rows: [
      [{ label: 'Have you ever been convicted of a crime?', type: 'yes_no', flexBasis: 2 }],
      [{ label: 'If yes, please describe the offense(s).', type: 'textarea', flexBasis: 2 }],
      [{ label: 'Date of most recent offense (if applicable)', type: 'date', flexBasis: 1 }],
      [{ label: 'Additional context or notes', type: 'textarea', flexBasis: 2 }]
    ]
  },
  {
    id: 'ssn_info_block',
    name: 'SSN Info Block',
    sectionTitle: 'SSN Information',
    rows: [
      [
        { label: 'Social Security Number', type: 'ssn', flexBasis: 1 },
        { label: 'Last 4 (optional)', type: 'text', flexBasis: 1 }
      ]
    ]
  },
  {
    id: 'consent_acknowledgement',
    name: 'Consent + Acknowledgement Block',
    sectionTitle: 'Consent and Acknowledgement',
    sectionDescription: 'Confirm policy acknowledgement before submission.',
    rows: [
      [{ label: 'I have read and understand the onboarding policies.', type: 'yes_no', flexBasis: 2 }],
      [{ label: 'I agree to comply with organization guidelines.', type: 'yes_no', flexBasis: 2 }],
      [{ label: 'Additional Notes', type: 'textarea', flexBasis: 2 }]
    ]
  }
];

const uid = (p = 'id') => `${p}_${Date.now()}_${Math.round(Math.random() * 1e5)}`;
const deepClone = (v) => JSON.parse(JSON.stringify(v));
const DND_FIELD_TYPE_MIME = 'application/x-onboarding-builder-field-type';
const DND_PREFORMED_MIME = 'application/x-onboarding-builder-preformed';
const DND_TEMPLATE_MIME = 'application/x-onboarding-builder-template';
const DND_CUSTOM_BLOCK_MIME = 'application/x-onboarding-builder-custom-block';
const getDefaultFieldLabel = (type) => {
  if (type === 'header') return 'Section Header';
  if (type === 'textinfo') return '';
  if (type === 'table') return 'Data Table';
  if (type === 'rich_textarea') return 'Rich Text Response';
  if (type === 'yes_no') return 'Type your yes or no question';
  if (type === 'true_false') return 'Type your true or false question';
  if (type === 'divider') return 'Divider';
  if (type === 'spacer') return 'Spacer';
  if (type === 'ssn') return 'SSN';
  if (type === 'signature') return 'Signature & Acknowledgement';
  return 'Untitled Field';
};
const getDisplayFieldLabel = (field) => {
  if (!field) return 'Untitled Field';
  const rawLabel = String(field.label || '').trim();
  if (rawLabel && rawLabel !== 'Untitled Field') return rawLabel;
  if (field.type === 'divider') return 'Divider';
  if (field.type === 'spacer') return 'Spacer';
  return rawLabel || getDefaultFieldLabel(field.type);
};
const isVerticalBooleanField = (field) =>
  (field?.type === 'yes_no' || field?.type === 'true_false') && (field.booleanLayout ?? 'horizontal') === 'vertical';

const createTableData = () => {
  const rows = 3;
  const cols = 3;
  const data = [];
  for (let r = 0; r < rows; r += 1) {
    const row = [];
    for (let c = 0; c < cols; c += 1) row.push({ id: uid('cell'), content: r === 0 ? `Header ${c + 1}` : '' });
    data.push(row);
  }
  return data;
};
const formatPhoneValue = (input) => {
  const digits = String(input || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
const formatSsnValue = (input) => {
  const digits = String(input || '').replace(/\D/g, '').slice(0, 9);
  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const createField = (type, seed = {}) => ({
  id: uid('field'),
  type,
  label: seed.label || getDefaultFieldLabel(type),
  placeholder: seed.placeholder || (type === 'yes_no' ? 'Type your yes or no question' : type === 'true_false' ? 'Type your true or false question' : ''),
  required: false,
  columns: 1,
  headingLevel: type === 'header' ? 'h2' : 'h3',
  headerAlignment: type === 'header' ? 'left' : undefined,
  headerSubtitle: type === 'header' ? '' : undefined,
  headerDescription: type === 'header' ? '' : undefined,
  tableTitle: type === 'table' ? '' : undefined,
  tableSubtitle: type === 'table' ? '' : undefined,
  tableDescription: type === 'table' ? '' : undefined,
  textContent: type === 'textinfo' ? 'Add informational text...' : '',
  dividerStyle: type === 'divider' ? 'solid' : undefined,
  dividerSpacing: type === 'divider' ? 16 : undefined,
  dividerThickness: type === 'divider' ? 1 : undefined,
  dividerColor: type === 'divider' ? 'var(--gray-400)' : undefined,
  spacerHeight: type === 'spacer' ? 24 : undefined,
  ssnMasked: type === 'ssn' ? true : undefined,
  conditionalRule: seed.conditionalRule || null,
  booleanLayout: type === 'yes_no' || type === 'true_false' ? (seed.booleanLayout ?? 'horizontal') : undefined,
  consentItems: type === 'signature'
    ? (seed.consentItems || [{ id: uid('consent'), text: 'I have read and agree to the terms above.', required: true }])
    : undefined,
  nameDisplayStyle: type === 'signature' ? (seed.nameDisplayStyle ?? 'cursive') : undefined,
  nameLabel: type === 'signature' ? (seed.nameLabel ?? 'Type your full legal name') : undefined,
  namePlaceholder: type === 'signature' ? (seed.namePlaceholder ?? 'First Last') : undefined,
  choiceTitle: type === 'dropdown' || type === 'checkbox' ? (seed.choiceTitle ?? '') : undefined,
  choiceDescription: type === 'dropdown' || type === 'checkbox' ? (seed.choiceDescription ?? '') : undefined,
  options: seed.options || (type === 'dropdown' || type === 'checkbox' ? [{ id: uid('opt'), value: 'option1', label: 'Option 1' }] : []),
  tableData: type === 'table' ? createTableData() : undefined
});

const createHeaderBlock = () => ({
  id: uid('container'),
  type: 'header_block',
  label: 'Header + Logo Block',
  isContainer: true,
  isHeaderBlock: true,
  required: false,
  columns: 1,
  logo: '',
  headerTitle: '',
  headerSubtitle: '',
  alignment: 'left',
  allowMultipleEntries: false,
  backgroundColor: 'transparent',
  borderSides: [],
  borderColor: 'var(--gray-400)',
  containerRows: [{
    id: uid('row'),
    fields: [{
      id: uid('field'),
      type: 'header_logo_content',
      label: 'Header + Logo',
      required: false,
      columns: 1,
      isHeaderLogoContent: true,
      flexBasis: 1
    }]
  }]
});

const getContainerRows = (container) => {
  if (Array.isArray(container?.containerRows) && container.containerRows.length > 0) return container.containerRows;
  if (Array.isArray(container?.nestedFields) && container.nestedFields.length > 0) return [{ id: uid('row'), fields: container.nestedFields }];
  return [];
};

const parseJsonResponse = async (res) => {
  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    const text = await res.text();
    throw new Error(`Unexpected non-JSON response (${res.status}): ${text.slice(0, 120)}`);
  }
  return res.json();
};

const OnboardingFormBuilderAdvancedPage = () => {
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const navigate = useNavigate();
  const { formId } = useParams();
  const isNew = !formId || formId === 'new';
  const canEdit = ['director', 'admin', 'attending'].includes(role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedNested, setDraggedNested] = useState(null); // { containerId, rowId, field }
  const [dragOverField, setDragOverField] = useState(null); // {index, position: top|center|bottom} index may be fields.length for end drop
  const [draggedPaletteType, setDraggedPaletteType] = useState(null); // FIELD_TYPES id
  const [draggedPreformedItem, setDraggedPreformedItem] = useState(null); // seed object for createField from preformed list
  const [draggedTemplateId, setDraggedTemplateId] = useState(null); // TEMPLATE_BLOCKS id (canvas insert only)
  const [draggedCustomBlockId, setDraggedCustomBlockId] = useState(null); // custom saved block id
  const [hoveredNested, setHoveredNested] = useState(null); // {containerId,nestedId}
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [fieldDraft, setFieldDraft] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'draft',
    schema: { fields: [] }
  });
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [draftMeta, setDraftMeta] = useState({ title: '', description: '' });
  /** Preformed sidebar: exactly one section open at a time; never fully collapsed */
  const [preformedAccordionKey, setPreformedAccordionKey] = useState('name');
  const [customBlocks, setCustomBlocks] = useState([]);
  const [showSaveCustomBlockModal, setShowSaveCustomBlockModal] = useState(false);
  const [customBlockName, setCustomBlockName] = useState('');
  const [pendingCustomFieldIdx, setPendingCustomFieldIdx] = useState(null);
  const [conditionAnswers, setConditionAnswers] = useState({});

  const togglePreviewMode = () => {
    setConditionAnswers({});
    setPreviewMode((v) => !v);
  };
  const [conditionalSelectionTarget, setConditionalSelectionTarget] = useState(null);
  const [showConditionalHelp, setShowConditionalHelp] = useState(false);
  const [showExitWarningModal, setShowExitWarningModal] = useState(false);
  const [pendingExitPath, setPendingExitPath] = useState(null);
  const initialFormSnapshotRef = useRef(null);
  const hasInitializedSnapshotRef = useRef(false);
  const bypassUnsavedWarningRef = useRef(false);
  const fieldDraftOptionListRef = useRef(null);
  const fieldDraftOptionSortableRef = useRef(null);

  const fields = useMemo(() => (Array.isArray(formData.schema?.fields) ? formData.schema.fields : []), [formData]);
  const setFields = (next) => setFormData((prev) => ({ ...prev, schema: { ...(prev.schema || {}), fields: next } }));
  const formSnapshot = useMemo(
    () => JSON.stringify({ title: formData.title || '', description: formData.description || '', schema: formData.schema || { fields: [] } }),
    [formData.title, formData.description, formData.schema]
  );
  const hasUnsavedChanges = hasInitializedSnapshotRef.current && initialFormSnapshotRef.current !== formSnapshot;
  const isBooleanSourceField = (f) => ['yes_no', 'true_false'].includes(f?.type);
  const evaluateConditionalRule = (rule, answerMap, entryId = 'legacy') => {
    if (!rule?.sourceFieldId) return true;
    const sourceKey = entryId === 'legacy' ? rule.sourceFieldId : `${rule.sourceFieldId}__entry_${entryId}`;
    return answerMap?.[sourceKey] === true;
  };
  const getSourceFieldLabelById = (sourceFieldId) => {
    for (const f of fields) {
      if (f?.id === sourceFieldId) return getDisplayFieldLabel(f);
      if (f?.isContainer) {
        for (const row of getContainerRows(f)) {
          for (const nf of (row.fields || [])) {
            if (nf?.id === sourceFieldId) return getDisplayFieldLabel(nf);
          }
        }
      }
    }
    return sourceFieldId;
  };

  useLayoutEffect(() => {
    const shouldSort =
      showFieldModal &&
      fieldDraft &&
      !fieldDraft.isContainer &&
      ['dropdown', 'checkbox'].includes(fieldDraft.type);
    if (!shouldSort) {
      if (fieldDraftOptionSortableRef.current) {
        fieldDraftOptionSortableRef.current.destroy();
        fieldDraftOptionSortableRef.current = null;
      }
      return undefined;
    }
    const el = fieldDraftOptionListRef.current;
    if (!el) return undefined;
    if (fieldDraftOptionSortableRef.current) {
      fieldDraftOptionSortableRef.current.destroy();
      fieldDraftOptionSortableRef.current = null;
    }
    fieldDraftOptionSortableRef.current = Sortable.create(el, {
      animation: 150,
      ghostClass: 'sortable-ghost',
      handle: '.field-option-drag-handle',
      onEnd: (evt) => {
        const oldIndex = evt.oldIndex;
        const newIndex = evt.newIndex;
        if (oldIndex == null || newIndex == null || oldIndex === newIndex) return;
        setFieldDraft((prev) => {
          if (!prev || !['dropdown', 'checkbox'].includes(prev.type)) return prev;
          const opts = [...(prev.options || [])];
          if (oldIndex < 0 || oldIndex >= opts.length || newIndex < 0 || newIndex > opts.length) return prev;
          const [moved] = opts.splice(oldIndex, 1);
          opts.splice(newIndex, 0, moved);
          return { ...prev, options: opts };
        });
      },
    });
    return () => {
      if (fieldDraftOptionSortableRef.current) {
        fieldDraftOptionSortableRef.current.destroy();
        fieldDraftOptionSortableRef.current = null;
      }
    };
  }, [showFieldModal, fieldDraft?.id, fieldDraft?.type]);

  useEffect(() => {
    const load = async () => {
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      if (isNew) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/forms/${formId}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('Failed to load form');
        const rawPayload = await parseJsonResponse(res);
        const payload = rawPayload?.form && typeof rawPayload.form === 'object' ? rawPayload.form : rawPayload;
        setFormData({
          title: payload.title || '',
          description: payload.description || '',
          status: payload.status || 'draft',
          schema: payload.schema && typeof payload.schema === 'object' ? payload.schema : { fields: [] }
        });
        const hdr = payload?.schema?.header && typeof payload.schema.header === 'object' ? payload.schema.header : null;
        if (hdr) {
          const mappedHeaderBlock = createHeaderBlock();
          mappedHeaderBlock.logo = hdr.logo || '';
          mappedHeaderBlock.headerTitle = hdr.title || '';
          mappedHeaderBlock.headerSubtitle = hdr.subtitle || '';
          mappedHeaderBlock.alignment = hdr.alignment || 'left';
          setFormData((prev) => {
            const currentFields = Array.isArray(prev.schema?.fields) ? prev.schema.fields : [];
            if (currentFields.some((f) => f?.isHeaderBlock)) return prev;
            return {
              ...prev,
              schema: {
                ...(prev.schema || {}),
                fields: [mappedHeaderBlock, ...currentFields]
              }
            };
          });
        }
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [formId, isNew, token]);

  useEffect(() => {
    if (!loading && !hasInitializedSnapshotRef.current) {
      initialFormSnapshotRef.current = formSnapshot;
      hasInitializedSnapshotRef.current = true;
    }
  }, [loading, formSnapshot]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges && !bypassUnsavedWarningRef.current) {
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return 'You have unsaved changes. Are you sure you want to leave?';
      }
      return undefined;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handleNavbarLinkClick = (event) => {
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || anchor.getAttribute('target') === '_blank') return;
      if (!hasUnsavedChanges || bypassUnsavedWarningRef.current) return;

      const url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return;

      event.preventDefault();
      setPendingExitPath(`${url.pathname}${url.search}${url.hash}`);
      setShowExitWarningModal(true);
    };

    document.addEventListener('click', handleNavbarLinkClick);
    return () => document.removeEventListener('click', handleNavbarLinkClick);
  }, [hasUnsavedChanges]);

  useEffect(() => {
    window.history.pushState(null, '', window.location.href);

    const handlePopState = () => {
      if (!hasUnsavedChanges || bypassUnsavedWarningRef.current) return;
      setPendingExitPath('/onboarding');
      setShowExitWarningModal(true);
      window.history.pushState(null, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [hasUnsavedChanges]);

  const handleAttemptExit = (path) => {
    if (hasUnsavedChanges && !bypassUnsavedWarningRef.current) {
      setPendingExitPath(path);
      setShowExitWarningModal(true);
      return;
    }
    navigate(path);
  };

  const handleLeaveWithoutSaving = () => {
    bypassUnsavedWarningRef.current = true;
    setShowExitWarningModal(false);
    navigate(pendingExitPath || '/onboarding');
  };

  const handleCancelExit = () => {
    setShowExitWarningModal(false);
    setPendingExitPath(null);
  };

  useEffect(() => {
    const loadCustomBlocks = async () => {
      if (!token || !canEdit) {
        setCustomBlocks([]);
        return;
      }
      try {
        const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/custom-blocks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to load custom blocks');
        const list = await parseJsonResponse(res);
        setCustomBlocks(Array.isArray(list) ? list : []);
      } catch (e) {
        setError(e.message || 'Failed to load custom blocks');
      }
    };
    loadCustomBlocks();
  }, [token, canEdit]);

  const addField = (type, seed = {}) => {
    const nextField = type === 'header_block' ? createHeaderBlock() : createField(type, seed);
    const nextFields = [...fields, nextField];
    setFields(nextFields);
    if (type === 'header_block' || type === 'header') {
      openModalForNewField(nextFields.length - 1, nextField);
    }
  };

  const buildTemplateBlock = (template) => {
    if (!template) return null;
    const rowSpecs = Array.isArray(template.rows) && template.rows.length > 0
      ? template.rows
      : [template.fields || []];
    const containerRows = rowSpecs.map((row) => ({
      id: uid('row'),
      fields: (row || []).map((item) => ({
        ...createField(item.type, item),
        flexBasis: item.flexBasis || 1
      }))
    }));
    return {
      id: uid('container'),
      type: 'container',
      label: template.name || 'Template Block',
      required: false,
      columns: 1,
      isContainer: true,
      allowMultipleEntries: false,
      sectionTitle: template.sectionTitle || template.name || 'Template Block',
      sectionSubtitle: template.sectionSubtitle || '',
      sectionDescription: template.sectionDescription || '',
      backgroundColor: 'transparent',
      borderSides: [],
      borderColor: 'var(--gray-400)',
      containerRows
    };
  };

  const addTemplateBlock = (template) => {
    const block = buildTemplateBlock(template);
    if (!block) return;
    setFields([...fields, block]);
  };

  const insertTemplateBlockAt = (template, insertIdx) => {
    const block = buildTemplateBlock(template);
    if (!block) return;
    const next = [...fields];
    const clamped = Math.max(0, Math.min(insertIdx, next.length));
    next.splice(clamped, 0, block);
    setFields(next);
  };
  const cloneBlockWithNewIds = (sourceBlock) => {
    const block = deepClone(sourceBlock);
    const idMap = {};
    const cloneField = (field) => {
      if (!field) return field;
      if (field.isContainer) {
        const oldContainerId = field.id;
        const container = { ...field, id: uid('container') };
        if (oldContainerId) idMap[oldContainerId] = container.id;
        container.containerRows = getContainerRows(field).map((row) => ({
          ...row,
          id: uid('row'),
          conditionalRule: row.conditionalRule ? { ...row.conditionalRule } : row.conditionalRule,
          fields: (row.fields || []).map((nf) => cloneField(nf))
        }));
        delete container.nestedFields;
        if (container.conditionalRule) container.conditionalRule = { ...container.conditionalRule };
        return container;
      }
      const oldFieldId = field.id;
      const next = { ...field, id: uid('field') };
      if (oldFieldId) idMap[oldFieldId] = next.id;
      delete next.__nestedCtx;
      if (next.conditionalRule) next.conditionalRule = { ...next.conditionalRule };
      if (Array.isArray(next.options)) {
        next.options = next.options.map((opt, index) => ({ ...opt, id: uid(`opt_${index}`) }));
      }
      if (Array.isArray(next.tableData)) {
        next.tableData = next.tableData.map((row) => (row || []).map((cell) => ({ ...cell, id: uid('cell') })));
      }
      return next;
    };
    const cloned = cloneField(block);
    const remapRule = (rule) => {
      if (!rule?.sourceFieldId) return rule;
      return { ...rule, sourceFieldId: idMap[rule.sourceFieldId] || rule.sourceFieldId };
    };
    const remapWithin = (field) => {
      if (!field) return field;
      field.conditionalRule = remapRule(field.conditionalRule);
      if (field.isContainer) {
        field.containerRows = getContainerRows(field).map((row) => ({
          ...row,
          conditionalRule: remapRule(row.conditionalRule),
          fields: (row.fields || []).map((nf) => remapWithin(nf))
        }));
      }
      return field;
    };
    return remapWithin(cloned);
  };

  const getCustomBlockById = (blockId) => customBlocks.find((b) => b.id === blockId) || null;

  const insertCustomBlockAt = (customBlock, insertIdx) => {
    if (!customBlock?.block) return;
    const hydrated = cloneBlockWithNewIds(customBlock.block);
    const next = [...fields];
    const clamped = Math.max(0, Math.min(insertIdx, next.length));
    next.splice(clamped, 0, hydrated);
    setFields(next);
  };

  const addCustomBlock = (customBlock) => {
    insertCustomBlockAt(customBlock, fields.length);
  };

  const openSaveCustomBlockModal = (idx) => {
    const source = fields[idx];
    if (!source) return;
    setPendingCustomFieldIdx(idx);
    setCustomBlockName(getDisplayFieldLabel(source) || `Block ${idx + 1}`);
    setShowSaveCustomBlockModal(true);
  };

  const confirmSaveCustomBlock = async () => {
    if (pendingCustomFieldIdx == null) return;
    const source = fields[pendingCustomFieldIdx];
    if (!source) return;
    const trimmed = customBlockName.trim();
    const name = trimmed || getDisplayFieldLabel(source) || 'Custom Block';
    try {
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/custom-blocks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name,
          block: deepClone(source)
        })
      });
      if (!res.ok) throw new Error('Failed to save custom block');
      const created = await parseJsonResponse(res);
      setCustomBlocks((prev) => [created, ...prev]);
      setShowSaveCustomBlockModal(false);
      setPendingCustomFieldIdx(null);
      setCustomBlockName('');
    } catch (e) {
      setError(e.message || 'Failed to save custom block');
    }
  };

  const removeCustomBlock = async (blockId) => {
    const target = getCustomBlockById(blockId);
    const label = target?.name || 'this custom block';
    const confirmed = window.confirm(`Delete "${label}"? It will be removed from Custom Blocks.`);
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_ENDPOINTS.ONBOARDING}/custom-blocks/${blockId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete custom block');
      setCustomBlocks((prev) => prev.filter((b) => b.id !== blockId));
    } catch (e) {
      setError(e.message || 'Failed to delete custom block');
    }
  };

  const removeField = (idx) => setFields(fields.filter((_, i) => i !== idx));

  const addContainerEntry = (idx) => {
    const source = fields[idx];
    if (!source?.isContainer || source.isHeaderBlock) return;
    const next = [...fields];
    next.splice(idx + 1, 0, cloneBlockWithNewIds(source));
    setFields(next);
  };

  const duplicateField = (idx) => {
    const source = fields[idx];
    const copy = source?.isContainer ? cloneBlockWithNewIds(source) : deepClone(source);
    copy.id = copy.isContainer ? copy.id : uid('field');
    copy.label = copy.type === 'header' || copy.type === 'textinfo' ? copy.label : `${copy.label} (Copy)`;
    if (copy.conditionalRule?.sourceFieldId === source?.id) {
      copy.conditionalRule = { ...copy.conditionalRule, sourceFieldId: copy.id };
    }
    if (Array.isArray(copy.options)) copy.options = copy.options.map((o, i) => ({ ...o, id: uid(`opt_${i}`) }));
    if (Array.isArray(copy.tableData)) {
      copy.tableData = copy.tableData.map((row) => row.map((cell) => ({ ...cell, id: uid('cell') })));
    }
    if (Array.isArray(copy.nestedFields)) copy.nestedFields = copy.nestedFields.map((f) => ({ ...f, id: uid('field') }));
    setFields([...fields, copy]);
  };

  const moveField = (idx, delta) => {
    const to = idx + delta;
    if (to < 0 || to >= fields.length) return;
    const next = [...fields];
    const tmp = next[idx];
    next[idx] = next[to];
    next[to] = tmp;
    setFields(next);
  };

  const openEditor = (idx, nestedId = null, rowId = null) => {
    setEditingFieldIndex({ idx, nestedId, rowId });
    if (nestedId) {
      const container = fields[idx];
      const rows = getContainerRows(container);
      const row = rows.find((r) => r.id === rowId) || rows[0];
      const nested = (row?.fields || []).find((n) => n.id === nestedId);
      if (nested?.isHeaderLogoContent) {
        setFieldDraft({
          ...deepClone(nested),
          logo: container.logo || '',
          headerTitle: container.headerTitle || '',
          headerSubtitle: container.headerSubtitle || '',
          alignment: container.alignment || 'left'
        });
      } else {
        setFieldDraft(deepClone(nested));
      }
    } else {
      setFieldDraft(deepClone(fields[idx]));
    }
    setShowFieldModal(true);
  };

  const openModalForNewField = (fieldIdx, field, nestedCtx) => {
    if (field?.isHeaderBlock) {
      const rows = getContainerRows(field);
      const row = rows[0];
      const nested = (row?.fields || []).find((n) => n?.isHeaderLogoContent);
      if (nested && row?.id) {
        setEditingFieldIndex({ idx: fieldIdx, nestedId: nested.id, rowId: row.id });
        setFieldDraft({
          ...deepClone(nested),
          logo: field.logo || '',
          headerTitle: field.headerTitle || '',
          headerSubtitle: field.headerSubtitle || '',
          alignment: field.alignment || 'left'
        });
      } else {
        setEditingFieldIndex({ idx: fieldIdx, nestedId: null, rowId: null });
        setFieldDraft(deepClone(field));
      }
      setShowFieldModal(true);
      return;
    }
    if (field?.type === 'header') {
      const { nestedId = null, rowId = null } = nestedCtx || {};
      setEditingFieldIndex({ idx: fieldIdx, nestedId, rowId });
      setFieldDraft(deepClone(field));
      setShowFieldModal(true);
    }
  };

  const saveEditor = () => {
    if (!editingFieldIndex || !fieldDraft) return;
    const normalizedDraft = (
      !fieldDraft.isContainer && ['dropdown', 'checkbox'].includes(fieldDraft.type)
        ? { ...fieldDraft, choiceTitle: fieldDraft.label || '' }
        : fieldDraft
    );
    const next = [...fields];
    if (editingFieldIndex.nestedId) {
      const container = next[editingFieldIndex.idx];
      const rows = getContainerRows(container).map((row) => (
        row.id !== editingFieldIndex.rowId
          ? row
          : { ...row, fields: (row.fields || []).map((n) => (n.id === editingFieldIndex.nestedId ? normalizedDraft : n)) }
      ));
      container.containerRows = rows;
      if (normalizedDraft.isHeaderLogoContent) {
        container.logo = normalizedDraft.logo || '';
        container.headerTitle = normalizedDraft.headerTitle || '';
        container.headerSubtitle = normalizedDraft.headerSubtitle || '';
        container.alignment = normalizedDraft.alignment || 'left';
      }
    } else {
      next[editingFieldIndex.idx] = normalizedDraft;
    }
    setFields(next);
    setShowFieldModal(false);
  };

  const updateTopLevelField = (fieldId, updater) => {
    setFields(fields.map((f) => (f.id === fieldId ? updater(f) : f)));
  };

  const updateNestedFieldValue = (containerId, rowId, nestedId, updater) => {
    setFields(fields.map((f) => {
      if (f.id !== containerId) return f;
      return {
        ...f,
        containerRows: getContainerRows(f).map((row) => (
          row.id !== rowId
            ? row
            : { ...row, fields: (row.fields || []).map((nf) => (nf.id === nestedId ? updater(nf) : nf)) }
        ))
      };
    }));
  };

  const beginConditionalSelection = (target) => {
    setConditionalSelectionTarget(target);
    setShowConditionalHelp(true);
  };

  const clearConditionalSelection = () => {
    setConditionalSelectionTarget(null);
    setShowConditionalHelp(false);
  };

  const canSelectSourceForConditionalTarget = (target, sourceField) => {
    if (!target?.kind || !sourceField || !isBooleanSourceField(sourceField)) return false;
    const sourceCtx = sourceField.__nestedCtx || null;
    if (target.kind === 'top') {
      if (sourceCtx) return false;
      return sourceField.id !== target.fieldId;
    }
    if (target.kind === 'nested') {
      if (!sourceCtx) return false;
      if (sourceCtx.containerId !== target.containerId) return false;
      return sourceField.id !== target.nestedId;
    }
    if (target.kind === 'row') {
      if (!sourceCtx) return false;
      return sourceCtx.containerId === target.containerId;
    }
    return false;
  };

  const applyConditionalSourceToTarget = (sourceFieldId) => {
    const target = conditionalSelectionTarget;
    if (!target?.kind || !sourceFieldId) return;
    const rule = { sourceFieldId, showWhen: 'true' };
    if (target.kind === 'top') {
      updateTopLevelField(target.fieldId, (f) => ({ ...f, conditionalRule: rule }));
    } else if (target.kind === 'nested') {
      updateNestedFieldValue(target.containerId, target.rowId, target.nestedId, (n) => ({ ...n, conditionalRule: rule }));
    } else if (target.kind === 'row') {
      setFields(fields.map((f) => (
        f.id !== target.containerId
          ? f
          : {
            ...f,
            containerRows: getContainerRows(f).map((row) => (row.id === target.rowId ? { ...row, conditionalRule: rule } : row))
          }
      )));
    }
    clearConditionalSelection();
  };

  const clearConditionalRuleForTarget = (target) => {
    if (!target?.kind) return;
    if (target.kind === 'top') {
      updateTopLevelField(target.fieldId, (f) => ({ ...f, conditionalRule: null }));
    } else if (target.kind === 'nested') {
      updateNestedFieldValue(target.containerId, target.rowId, target.nestedId, (n) => ({ ...n, conditionalRule: null }));
    } else if (target.kind === 'row') {
      setFields(fields.map((f) => (
        f.id !== target.containerId
          ? f
          : {
            ...f,
            containerRows: getContainerRows(f).map((row) => (row.id === target.rowId ? { ...row, conditionalRule: null } : row))
          }
      )));
    }
  };

  const addTableRow = (fieldId, rowIndex, position) => {
    setFields(fields.map((f) => {
      if (f.id !== fieldId || !Array.isArray(f.tableData) || !f.tableData[0]) return f;
      const row = f.tableData[0].map(() => ({ id: uid('cell'), content: '' }));
      const table = [...f.tableData];
      table.splice(position === 'above' ? rowIndex : rowIndex + 1, 0, row);
      return { ...f, tableData: table };
    }));
  };

  const addTableColumn = (fieldId, colIndex, position) => {
    setFields(fields.map((f) => {
      if (f.id !== fieldId || !Array.isArray(f.tableData)) return f;
      const table = f.tableData.map((row, r) => {
        const copy = [...row];
        copy.splice(position === 'left' ? colIndex : colIndex + 1, 0, { id: uid('cell'), content: r === 0 ? `Header ${copy.length}` : '' });
        return copy;
      });
      return { ...f, tableData: table };
    }));
  };

  const deleteTableColumn = (fieldId, colIndex) => {
    setFields(fields.map((f) => {
      if (f.id !== fieldId || !Array.isArray(f.tableData) || !f.tableData[0] || f.tableData[0].length <= 1) return f;
      const table = f.tableData.map((row) => row.filter((_, c) => c !== colIndex));
      return { ...f, tableData: table };
    }));
  };

  const deleteTableRow = (fieldId, rowIndex) => {
    setFields(fields.map((f) => {
      if (f.id !== fieldId || !Array.isArray(f.tableData) || f.tableData.length <= 1) return f;
      const table = f.tableData.filter((_, r) => r !== rowIndex);
      return { ...f, tableData: table };
    }));
  };

  const updateTableCell = (fieldId, rowIndex, colIndex, content) => {
    setFields(fields.map((f) => {
      if (f.id !== fieldId || !Array.isArray(f.tableData)) return f;
      const table = f.tableData.map((row, r) => (r === rowIndex ? row.map((cell, c) => (c === colIndex ? { ...cell, content } : cell)) : row));
      return { ...f, tableData: table };
    }));
  };

  const adjustNestedFieldWidth = (containerId, nestedFieldId, increase) => {
    setFields(fields.map((f) => {
      if (f.id !== containerId) return f;
      const rows = getContainerRows(f).map((row) => ({
        ...row,
        fields: (row.fields || []).map((nf) => {
          if (nf.id !== nestedFieldId) return nf;
          const base = nf.flexBasis || 1;
          const next = increase ? base + 0.5 : Math.max(0.5, base - 0.5);
          return { ...nf, flexBasis: next };
        })
      }));
      return {
        ...f,
        containerRows: rows
      };
    }));
  };

  const moveNestedField = (containerId, nestedFieldId, direction) => {
    setFields(fields.map((f) => {
      if (f.id !== containerId) return f;
      const rows = getContainerRows(f).map((row) => {
        const idx = (row.fields || []).findIndex((nf) => nf.id === nestedFieldId);
        if (idx === -1) return row;
        const to = direction === 'left' ? idx - 1 : idx + 1;
        if (to < 0 || to >= row.fields.length) return row;
        const nested = [...row.fields];
        const tmp = nested[idx];
        nested[idx] = nested[to];
        nested[to] = tmp;
        return { ...row, fields: nested };
      });
      return { ...f, containerRows: rows };
    }));
  };

  const removeNestedField = (containerId, rowId, nestedFieldId) => {
    setFields(fields.map((f) => {
      if (f.id !== containerId) return f;
      return {
        ...f,
        containerRows: getContainerRows(f).map((row) => (
          row.id !== rowId
            ? row
            : { ...row, fields: (row.fields || []).filter((nf) => nf.id !== nestedFieldId) }
        ))
      };
    }));
  };

  const addContainerRow = (containerId) => {
    setFields(fields.map((f) => {
      if (f.id !== containerId) return f;
      const rows = getContainerRows(f);
      return { ...f, containerRows: [...rows, { id: uid('row'), fields: [] }] };
    }));
  };

  const removeContainerRow = (containerId, rowId) => {
    setFields(fields.map((f) => {
      if (f.id !== containerId) return f;
      const rows = getContainerRows(f).filter((r) => r.id !== rowId);
      return { ...f, containerRows: rows };
    }));
  };

  const clearBuilderDnD = () => {
    setDraggedIndex(null);
    setDraggedNested(null);
    setDragOverField(null);
    setDraggedPaletteType(null);
    setDraggedPreformedItem(null);
    setDraggedTemplateId(null);
    setDraggedCustomBlockId(null);
  };

  const moveNestedToRow = (targetContainerId, targetRowId) => {
    if (!draggedNested) return;
    setFields(fields.map((f) => {
      if (!f.isContainer) return f;
      const rows = getContainerRows(f).map((r) => ({ ...r, fields: [...(r.fields || [])] }));
      if (f.id === draggedNested.containerId) {
        const sourceRow = rows.find((r) => r.id === draggedNested.rowId);
        if (sourceRow) sourceRow.fields = sourceRow.fields.filter((x) => x.id !== draggedNested.field.id);
      }
      if (f.id === targetContainerId) {
        const targetRow = rows.find((r) => r.id === targetRowId);
        if (targetRow) targetRow.fields.push({ ...deepClone(draggedNested.field), id: uid('field') });
      }
      return { ...f, containerRows: rows };
    }));
    clearBuilderDnD();
  };

  const isCanvasEndIndex = (idx) => idx === fields.length;

  const extractNestedToTopLevel = (targetIndex, position) => {
    if (!draggedNested) return;
    const { containerId, rowId, field } = draggedNested;
    const extracted = { ...deepClone(field), id: uid('field') };
    delete extracted.__nestedCtx;
    const next = fields.map((f) => {
      if (f.id !== containerId) return f;
      return {
        ...f,
        containerRows: getContainerRows(f).map((row) => (
          row.id !== rowId ? row : { ...row, fields: (row.fields || []).filter((nf) => nf.id !== field.id) }
        ))
      };
    });
    const insertIdx = position === 'top' ? targetIndex : targetIndex + 1;
    const clamped = Math.max(0, Math.min(insertIdx, next.length));
    setFields([...next.slice(0, clamped), extracted, ...next.slice(clamped)]);
  };

  const extractNestedToAppendEnd = () => {
    if (!draggedNested) return;
    const { containerId, rowId, field } = draggedNested;
    const extracted = { ...deepClone(field), id: uid('field') };
    delete extracted.__nestedCtx;
    const next = fields.map((f) => {
      if (f.id !== containerId) return f;
      return {
        ...f,
        containerRows: getContainerRows(f).map((row) => (
          row.id !== rowId ? row : { ...row, fields: (row.fields || []).filter((nf) => nf.id !== field.id) }
        ))
      };
    });
    next.push(extracted);
    setFields(next);
  };

  const appendFieldToContainerRow = (containerId, rowId, nestedField) => {
    const nf = { ...nestedField, id: nestedField.id || uid('field'), flexBasis: nestedField.flexBasis || 1 };
    setFields(fields.map((f) => {
      if (f.id !== containerId) return f;
      return {
        ...f,
        containerRows: getContainerRows(f).map((row) => (
          row.id !== rowId ? row : { ...row, fields: [...(row.fields || []), nf] }
        ))
      };
    }));
  };

  /** Row / in-container drops must stopPropagation so the parent canvas block onDrop does not also run. */
  const applyDropTargetToContainerRow = (containerId, rowId) => {
    if (draggedTemplateId) {
      clearBuilderDnD();
      return true;
    }
    if (draggedCustomBlockId) {
      const customBlock = getCustomBlockById(draggedCustomBlockId);
      if (!customBlock?.block) {
        clearBuilderDnD();
        return true;
      }
      const hydrated = cloneBlockWithNewIds(customBlock.block);
      if (hydrated?.isContainer) {
        clearBuilderDnD();
        return true;
      }
      appendFieldToContainerRow(containerId, rowId, hydrated);
      clearBuilderDnD();
      return true;
    }
    if (draggedPaletteType) {
      if (draggedPaletteType === 'header_block') {
        clearBuilderDnD();
        return true;
      }
      const newField = createField(draggedPaletteType, {});
      const containerIdx = fields.findIndex((f) => f.id === containerId);
      appendFieldToContainerRow(containerId, rowId, newField);
      if (newField.type === 'header' && containerIdx >= 0) {
        openModalForNewField(containerIdx, newField, { nestedId: newField.id, rowId });
      }
      clearBuilderDnD();
      return true;
    }
    if (draggedPreformedItem) {
      const newField = createField(draggedPreformedItem.type, draggedPreformedItem);
      const containerIdx = fields.findIndex((f) => f.id === containerId);
      appendFieldToContainerRow(containerId, rowId, newField);
      if (newField.type === 'header' && containerIdx >= 0) {
        openModalForNewField(containerIdx, newField, { nestedId: newField.id, rowId });
      }
      clearBuilderDnD();
      return true;
    }
    if (draggedNested) {
      moveNestedToRow(containerId, rowId);
      return true;
    }
    return false;
  };

  const handleDropOnContainerRow = (e, containerId, rowId) => {
    e.preventDefault();
    e.stopPropagation();
    if (previewMode) return;
    applyDropTargetToContainerRow(containerId, rowId);
  };

  /** Section title / padding inside a container (not on a row strip): still nest into last row. */
  const handleDropOnContainerBody = (e, containerField) => {
    e.preventDefault();
    e.stopPropagation();
    if (previewMode) return;
    const rows = getContainerRows(containerField);
    const lastRow = rows[rows.length - 1];
    if (!lastRow) return;
    applyDropTargetToContainerRow(containerField.id, lastRow.id);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (previewMode) return;
    if (isCanvasEndIndex(index)) {
      if (draggedIndex != null || draggedNested || draggedPaletteType || draggedPreformedItem || draggedTemplateId || draggedCustomBlockId) {
        e.dataTransfer.dropEffect = draggedNested || draggedIndex != null ? 'move' : 'copy';
        setDragOverField({ index, position: 'bottom' });
      }
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    if (draggedNested) {
      if (y < rect.height * 0.5) setDragOverField({ index, position: 'top' });
      else setDragOverField({ index, position: 'bottom' });
      return;
    }
    if (draggedPaletteType || draggedPreformedItem || draggedTemplateId || draggedCustomBlockId) {
      if (y < rect.height * 0.3) setDragOverField({ index, position: 'top' });
      else if (y > rect.height * 0.7) setDragOverField({ index, position: 'bottom' });
      else setDragOverField({ index, position: 'center' });
      return;
    }
    if (draggedIndex == null || draggedIndex === index) return;
    if (y < rect.height * 0.3) setDragOverField({ index, position: 'top' });
    else if (y > rect.height * 0.7) setDragOverField({ index, position: 'bottom' });
    else setDragOverField({ index, position: 'center' });
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (previewMode) return;

    if (isCanvasEndIndex(index)) {
      if (draggedNested) {
        extractNestedToAppendEnd();
        clearBuilderDnD();
        return;
      }
      if (draggedTemplateId) {
        const template = TEMPLATE_BLOCKS.find((t) => t.id === draggedTemplateId);
        if (template) insertTemplateBlockAt(template, fields.length);
        clearBuilderDnD();
        return;
      }
      if (draggedCustomBlockId) {
        const customBlock = getCustomBlockById(draggedCustomBlockId);
        if (customBlock) addCustomBlock(customBlock);
        clearBuilderDnD();
        return;
      }
      if (draggedPaletteType || draggedPreformedItem) {
        let newField;
        if (draggedPaletteType === 'header_block') {
          newField = createHeaderBlock();
        } else if (draggedPaletteType) {
          newField = createField(draggedPaletteType, {});
        } else {
          newField = createField(draggedPreformedItem.type, draggedPreformedItem);
        }
        const nextFields = [...fields, newField];
        setFields(nextFields);
        if (draggedPaletteType === 'header_block' || draggedPaletteType === 'header' || draggedPreformedItem?.type === 'header') {
          openModalForNewField(nextFields.length - 1, newField);
        }
        clearBuilderDnD();
        return;
      }
      if (draggedIndex != null) {
        const next = [...fields];
        const dragged = next[draggedIndex];
        next.splice(draggedIndex, 1);
        next.push(dragged);
        setFields(next);
        clearBuilderDnD();
        return;
      }
      clearBuilderDnD();
      return;
    }

    if (draggedNested) {
      extractNestedToTopLevel(index, dragOverField?.position === 'top' ? 'top' : 'bottom');
      clearBuilderDnD();
      return;
    }

    if (draggedTemplateId) {
      const template = TEMPLATE_BLOCKS.find((t) => t.id === draggedTemplateId);
      if (!template) {
        clearBuilderDnD();
        return;
      }
      const pos = dragOverField?.position || 'bottom';
      const insertIdx = pos === 'top' ? index : index + 1;
      const clamped = Math.max(0, Math.min(insertIdx, fields.length));
      if (pos === 'center') {
        insertTemplateBlockAt(template, index + 1);
      } else {
        insertTemplateBlockAt(template, clamped);
      }
      clearBuilderDnD();
      return;
    }

    if (draggedCustomBlockId) {
      const customBlock = getCustomBlockById(draggedCustomBlockId);
      if (!customBlock) {
        clearBuilderDnD();
        return;
      }
      const pos = dragOverField?.position || 'bottom';
      const insertIdx = pos === 'top' ? index : index + 1;
      const clamped = Math.max(0, Math.min(insertIdx, fields.length));
      insertCustomBlockAt(customBlock, clamped);
      clearBuilderDnD();
      return;
    }

    if (draggedPaletteType || draggedPreformedItem) {
      let newField;
      if (draggedPaletteType === 'header_block') {
        newField = createHeaderBlock();
      } else if (draggedPaletteType) {
        newField = createField(draggedPaletteType, {});
      } else {
        newField = createField(draggedPreformedItem.type, draggedPreformedItem);
      }
      const next = [...fields];
      const pos = dragOverField?.position || 'bottom';
      const insertIdx = pos === 'top' ? index : index + 1;
      const clamped = Math.max(0, Math.min(insertIdx, next.length));
      let pendingNewFieldModal = null;
      if (pos === 'center' && newField.isHeaderBlock) {
        next.splice(index + 1, 0, newField);
        setFields(next);
        pendingNewFieldModal = { idx: index + 1, field: newField };
      } else if (pos === 'center') {
        const target = next[index];
        if (target?.isContainer) {
          const rows = getContainerRows(target);
          const nestedField = { ...newField, id: uid('field'), flexBasis: 1 };
          if (rows.length > 0) {
            rows[rows.length - 1].fields = [...(rows[rows.length - 1].fields || []), nestedField];
          } else {
            rows.push({ id: uid('row'), fields: [nestedField] });
          }
          target.containerRows = rows;
          setFields(next);
          if (newField.type === 'header') {
            const row = rows[rows.length - 1];
            pendingNewFieldModal = { idx: index, field: nestedField, nestedId: nestedField.id, rowId: row.id };
          }
        } else {
          const nestedField = { ...newField, id: uid('field'), flexBasis: 1 };
          const row = { id: uid('row'), fields: [{ ...deepClone(target), id: uid('field'), columns: 1, flexBasis: 1 }, nestedField] };
          const container = {
            id: uid('container'),
            type: 'container',
            label: 'Multi-Column Container',
            required: false,
            columns: target.columns || 1,
            isContainer: true,
            allowMultipleEntries: false,
            sectionTitle: '',
            sectionSubtitle: '',
            sectionDescription: '',
            backgroundColor: 'transparent',
            borderSides: ['all'],
            borderColor: 'var(--gray-400)',
            containerRows: [row]
          };
          next.splice(index, 1, container);
          setFields(next);
          if (newField.type === 'header') {
            pendingNewFieldModal = { idx: index, field: nestedField, nestedId: nestedField.id, rowId: row.id };
          }
        }
      } else {
        next.splice(clamped, 0, newField);
        setFields(next);
        if (newField.isHeaderBlock || newField.type === 'header') {
          pendingNewFieldModal = { idx: clamped, field: newField };
        }
      }
      if (pendingNewFieldModal) {
        const { idx: modalIdx, field: modalField, nestedId, rowId } = pendingNewFieldModal;
        openModalForNewField(modalIdx, modalField, nestedId != null ? { nestedId, rowId } : undefined);
      }
      clearBuilderDnD();
      return;
    }

    if (draggedIndex == null || draggedIndex === index) {
      clearBuilderDnD();
      return;
    }
    const next = [...fields];
    const dragged = next[draggedIndex];
    if (dragOverField?.position === 'center') {
      const target = next[index];
      if (target.isContainer) {
        const rows = getContainerRows(target);
        if (rows.length > 0) {
          rows[rows.length - 1].fields = [...(rows[rows.length - 1].fields || []), { ...deepClone(dragged), id: uid('field'), columns: 1, flexBasis: 1 }];
        } else {
          rows.push({ id: uid('row'), fields: [{ ...deepClone(dragged), id: uid('field'), columns: 1, flexBasis: 1 }] });
        }
        target.containerRows = rows;
        next.splice(draggedIndex, 1);
      } else {
        const container = {
          id: uid('container'),
          type: 'container',
          label: 'Multi-Column Container',
          required: false,
          columns: target.columns || 1,
          isContainer: true,
          allowMultipleEntries: false,
          sectionTitle: '',
          sectionSubtitle: '',
          sectionDescription: '',
          backgroundColor: 'transparent',
          borderSides: ['all'],
          borderColor: 'var(--gray-400)',
          containerRows: [{ id: uid('row'), fields: [{ ...deepClone(target), id: uid('field'), columns: 1, flexBasis: 1 }, { ...deepClone(dragged), id: uid('field'), columns: 1, flexBasis: 1 }] }]
        };
        const max = Math.max(draggedIndex, index);
        const min = Math.min(draggedIndex, index);
        next.splice(max, 1);
        next.splice(min, 1, container);
      }
    } else if (dragOverField?.position === 'bottom') {
      const target = next[index];
      if (target.isContainer) {
        const rows = getContainerRows(target);
        rows.push({ id: uid('row'), fields: [{ ...deepClone(dragged), id: uid('field'), columns: 1, flexBasis: 1 }] });
        target.containerRows = rows;
        next.splice(draggedIndex, 1);
      } else {
        const container = {
          id: uid('container'),
          type: 'container',
          label: 'Multi-Column Container',
          required: false,
          columns: target.columns || 1,
          isContainer: true,
          allowMultipleEntries: false,
          sectionTitle: '',
          sectionSubtitle: '',
          sectionDescription: '',
          backgroundColor: 'transparent',
          borderSides: ['all'],
          borderColor: 'var(--gray-400)',
          containerRows: [
            { id: uid('row'), fields: [{ ...deepClone(target), id: uid('field'), columns: 1, flexBasis: 1 }] },
            { id: uid('row'), fields: [{ ...deepClone(dragged), id: uid('field'), columns: 1, flexBasis: 1 }] }
          ]
        };
        const max = Math.max(draggedIndex, index);
        const min = Math.min(draggedIndex, index);
        next.splice(max, 1);
        next.splice(min, 1, container);
      }
    } else {
      next.splice(draggedIndex, 1);
      next.splice(draggedIndex < index ? index - 1 : index, 0, dragged);
    }
    setFields(next);
    clearBuilderDnD();
  };

  const saveForm = async (nextStatus = null, shouldExit = false) => {
    try {
      setSaving(true);
      setError('');
      const payload = {
        title: formData.title.trim(),
        description: formData.description || null,
        status: nextStatus || formData.status || 'draft',
        schema: { ...(formData.schema || {}), fields }
      };
      if (!payload.title) throw new Error('Title is required');
      if (payload.status === 'draft' && !payload.description) throw new Error('Description is required for drafts');
      const url = isNew ? `${API_ENDPOINTS.ONBOARDING}/forms` : `${API_ENDPOINTS.ONBOARDING}/forms/${formId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b?.error || 'Failed to save form');
      }
      const savedRaw = await parseJsonResponse(res);
      const saved = savedRaw?.form && typeof savedRaw.form === 'object' ? savedRaw.form : savedRaw;
      initialFormSnapshotRef.current = formSnapshot;
      if (shouldExit) {
        bypassUnsavedWarningRef.current = true;
        navigate('/onboarding');
      } else if (isNew) {
        bypassUnsavedWarningRef.current = true;
        navigate(`/onboarding/forms/${saved.id}/edit`);
      }
      setFormData((prev) => ({ ...prev, status: saved.status || payload.status }));
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openDraftSaveModal = () => {
    setDraftMeta({
      title: formData.title || '',
      description: formData.description || ''
    });
    setShowDraftModal(true);
  };

  const handleConfirmDraftSave = async () => {
    await handleConfirmDraftSaveWithExit(false);
  };

  const handleConfirmDraftSaveWithExit = async (shouldExit) => {
    const title = draftMeta.title.trim();
    const description = draftMeta.description.trim();
    if (!title || !description) {
      setError('Draft requires both title and description');
      return;
    }
    setFormData((prev) => ({ ...prev, title, description }));
    setShowDraftModal(false);
    await saveForm('draft', shouldExit);
  };

  const openPublishModal = () => {
    setShowPublishModal(true);
  };

  const handlePublish = async (shouldExit = false) => {
    setShowPublishModal(false);
    await saveForm('published', shouldExit);
  };

  const getContainerStyle = (field) => {
    const style = {
      backgroundColor: field.backgroundColor || 'transparent'
    };
    const borderColor = field.borderColor || 'var(--gray-400)';
    const sides = Array.isArray(field.borderSides) ? field.borderSides : [];
    if (sides.includes('all')) {
      style.border = `2px solid ${borderColor}`;
    } else {
      if (sides.includes('top')) style.borderTop = `2px solid ${borderColor}`;
      if (sides.includes('right')) style.borderRight = `2px solid ${borderColor}`;
      if (sides.includes('bottom')) style.borderBottom = `2px solid ${borderColor}`;
      if (sides.includes('left')) style.borderLeft = `2px solid ${borderColor}`;
    }
    return style;
  };

  const renderField = (field, parentContainer = null) => {
    if (field.type === 'header') {
      const tag = field.headingLevel || 'h3';
      const className = tag === 'h1' ? 'h1' : tag === 'h2' ? 'h2' : tag === 'h4' ? 'h4' : tag === 'h5' ? 'h5' : tag === 'h6' ? 'h6' : 'h3';
      const alignment = field.headerAlignment || 'left';
      const alignmentClass = alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-end' : 'text-start';
      return (
        <div className={alignmentClass}>
          <div className={`${className} mb-1`}>{field.label || 'Header'}</div>
          {field.headerSubtitle ? <div className="text-muted fw-semibold mb-0">{field.headerSubtitle}</div> : null}
          {field.headerDescription ? <p className="text-muted small mb-2">{field.headerDescription}</p> : null}
        </div>
      );
    }
    if (field.type === 'header_logo_content') {
      const container = parentContainer || {};
      return (
        <div className={`d-flex flex-row gap-2 align-items-center ${container.alignment === 'center' ? 'justify-content-center' : container.alignment === 'right' ? 'justify-content-end' : 'justify-content-start'}`}>
          {container.logo ? <img src={container.logo} alt="Form logo" style={{ maxHeight: 36, objectFit: 'contain' }} /> : null}
          <div className="d-flex flex-column">
          {container.headerTitle ? <h6 className="mb-0 fw-semibold">{container.headerTitle}</h6> : null}
          {container.headerSubtitle ? <p className="text-muted mb-0">{container.headerSubtitle}</p> : null}
          </div>
          {!container.logo && !container.headerTitle && !container.headerSubtitle && (
            <div className="text-muted small">Open settings to add logo/header content</div>
          )}
        </div>
      );
    }
    if (field.type === 'textinfo') {
      if (!previewMode) {
        return (
          <RichTextEditor
            value={field.textContent || ''}
            onChange={(nextValue) => {
              if (field.__nestedCtx) {
                updateNestedFieldValue(field.__nestedCtx.containerId, field.__nestedCtx.rowId, field.id, (nf) => ({ ...nf, textContent: nextValue }));
              } else {
                updateTopLevelField(field.id, (f) => ({ ...f, textContent: nextValue }));
              }
            }}
            placeholder="Add informational text..."
          />
        );
      }
      return <div className="text-muted mb-2" dangerouslySetInnerHTML={{ __html: field.textContent || '' }} />;
    }
    if (field.type === 'divider') {
      const spacing = Number(field.dividerSpacing ?? 16);
      const dividerStyle = field.dividerStyle || 'solid';
      const dividerThickness = Number(field.dividerThickness || 1);
      const dividerColor = field.dividerColor || 'var(--gray-400)';
      if (!previewMode) {
        return (
          <div className="pt-1 pb-1">
            <hr className="m-0" style={{ borderTop: `${dividerThickness}px ${dividerStyle} ${dividerColor}` }} />
          </div>
        );
      }
      return (
        <div style={{ paddingTop: spacing, paddingBottom: spacing }}>
          <hr className="m-0" style={{ borderTop: `${dividerThickness}px ${dividerStyle} ${dividerColor}` }} />
        </div>
      );
    }
    if (field.type === 'spacer') {
      const spacerHeight = Number(field.spacerHeight || 24);
      if (previewMode) return <div style={{ height: spacerHeight }} />;
      return null;
    }
    if (field.type === 'table' && Array.isArray(field.tableData)) {
      return (
        <div className="d-flex flex-column gap-1 p-2">
          {field.tableTitle ? <div className="fw-semibold">{field.tableTitle}</div> : null}
          {field.tableSubtitle ? <div className="text-muted fw-semibold">{field.tableSubtitle}</div> : null}
          {field.tableDescription ? <p className="text-muted small mb-1">{field.tableDescription}</p> : null}
          <FormBuilderEditableTable
            field={field}
            previewMode={previewMode}
            addTableColumn={addTableColumn}
            addTableRow={addTableRow}
            deleteTableColumn={deleteTableColumn}
            deleteTableRow={deleteTableRow}
            updateTableCell={updateTableCell}
          />
        </div>
      );
    }
    if (field.type === 'textarea') return <Form.Control as="textarea" size="sm" rows={3} placeholder={field.placeholder || ''} disabled={!previewMode} />;
    if (field.type === 'rich_textarea') {
      return <div className="border rounded p-2 text-muted bg-light">Rich text response field</div>;
    }
    if (field.type === 'dropdown') {
      const displayChoiceTitle = field.choiceTitle || getDisplayFieldLabel(field);
      const shouldShowChoiceTitle = previewMode && Boolean(displayChoiceTitle);
      const selectEl = (
        <Form.Select size="sm" disabled={!previewMode}>
          <option value="">Select...</option>
          {(field.options || []).map((o) => (
            <option key={o.id} value={o.value ?? o.label}>
              {o.label}
            </option>
          ))}
        </Form.Select>
      );
      return (
        <div className="d-flex flex-column gap-1 w-100">
          {shouldShowChoiceTitle ? (
            <div className="fw-semibold small mb-1">
              {displayChoiceTitle}
              {field.required ? ' *' : ''}
            </div>
          ) : previewMode && field.required ? (
            <div className="fw-semibold small">*</div>
          ) : null}
          {field.choiceDescription ? <div className="text-muted small">{field.choiceDescription}</div> : null}
          {selectEl}
        </div>
      );
    }
    if (field.type === 'checkbox') {
      const displayChoiceTitle = field.choiceTitle || getDisplayFieldLabel(field);
      const shouldShowChoiceTitle = previewMode && Boolean(displayChoiceTitle);
      const checks = (
        <div className="d-flex flex-column gap-1">
          {(field.options || []).map((o) => (
            <Form.Check key={o.id} type="checkbox" label={o.label} disabled={!previewMode} />
          ))}
        </div>
      );
      return (
        <div className="d-flex flex-column gap-1 w-100">
          {shouldShowChoiceTitle ? (
            <div className="fw-semibold small mb-1">
              {displayChoiceTitle}
              {field.required ? ' *' : ''}
            </div>
          ) : previewMode && field.required ? (
            <div className="fw-semibold small">*</div>
          ) : null}
          {field.choiceDescription ? <div className="text-muted small">{field.choiceDescription}</div> : null}
          {checks}
        </div>
      );
    }
    if (field.type === 'signature') {
      const sigValue = previewMode ? conditionAnswers?.[field.id] : { status: 'unsigned', draftConsents: {}, draftSignerName: '' };
      const buildPreviewSigned = ({ signerName, consents: clientConsents }) => {
        const items = Array.isArray(field.consentItems) ? field.consentItems : [];
        const clientMap = new Map((clientConsents || []).map((c) => [String(c.id), Boolean(c.checked)]));
        const now = new Date().toISOString();
        const consentSnapshot = items.map((item) => ({
          id: item.id,
          text: String(item.text || ''),
          required: item.required !== false,
          checked: Boolean(clientMap.get(String(item.id))),
          checkedAt: clientMap.get(String(item.id)) ? now : null
        }));
        const displayStyle = field.nameDisplayStyle === 'typed' ? 'typed' : 'cursive';
        return {
          status: 'signed',
          signerName: String(signerName).trim(),
          signedAt: now,
          consents: consentSnapshot,
          signatureDisplay: { style: displayStyle, renderedText: String(signerName).trim() },
          audit: { signedAt: now, preview: true }
        };
      };
      return (
        <SignatureField
          field={field}
          value={sigValue}
          readOnly={!previewMode}
          allowReset={previewMode}
          onReset={() => {
            setConditionAnswers((prev) => {
              const next = { ...prev };
              delete next[field.id];
              return next;
            });
          }}
          onDraftChange={(draft) => {
            if (!previewMode) return;
            setConditionAnswers((prev) => ({ ...prev, [field.id]: draft }));
          }}
          onSign={async (payload) => {
            if (!previewMode) return;
            setConditionAnswers((prev) => ({ ...prev, [field.id]: buildPreviewSigned(payload) }));
          }}
        />
      );
    }
    if (field.type === 'yes_no' || field.type === 'true_false') {
      const positiveLabel = field.type === 'yes_no' ? 'Yes' : 'True';
      const negativeLabel = field.type === 'yes_no' ? 'No' : 'False';
      const questionText = getDisplayFieldLabel(field);
      const selectedValue = conditionAnswers?.[field.id];
      const canPickAsSource = canSelectSourceForConditionalTarget(conditionalSelectionTarget, field);
      const sourceCandidateHighlight = Boolean(conditionalSelectionTarget) && canPickAsSource;
      const isVerticalLayout = isVerticalBooleanField(field);
      const hideLabelInBuilder = isVerticalLayout && !previewMode;
      return (
        <div className={isVerticalLayout ? (previewMode ? 'd-flex flex-column align-items-start gap-2 w-100' : 'w-100') : 'd-flex align-items-center justify-content-between gap-2 w-100'}>
          {!hideLabelInBuilder && (
            <span className="fw-semibold ">{questionText}{field.required ? ' *' : ''}</span>
          )}
          <ButtonGroup size="sm">
            <Button
              type="button"
              variant={sourceCandidateHighlight ? 'primary' : selectedValue === true ? 'primary' : 'outline-secondary'}
              onClick={() => {
                if (canPickAsSource) {
                  applyConditionalSourceToTarget(field.id);
                  return;
                }
                setConditionAnswers((prev) => ({ ...prev, [field.id]: true }));
              }}
              disabled={Boolean(conditionalSelectionTarget) ? !canPickAsSource : !previewMode}
            >
              {positiveLabel}
            </Button>
            <Button
              type="button"
              variant={sourceCandidateHighlight ? 'primary' : selectedValue === false ? 'primary' : 'outline-secondary'}
              onClick={() => {
                if (canPickAsSource) {
                  applyConditionalSourceToTarget(field.id);
                  return;
                }
                setConditionAnswers((prev) => ({ ...prev, [field.id]: false }));
              }}
              disabled={Boolean(conditionalSelectionTarget) ? !canPickAsSource : !previewMode}
            >
              {negativeLabel}
            </Button>
          </ButtonGroup>
        </div>
      );
    }
    return (
      <Form.Control
        size="sm"
        type={field.type === 'ssn' ? 'password' : field.type === 'phone' ? 'tel' : field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'date' ? 'date' : 'text'}
        placeholder={field.placeholder || (field.type === 'phone' ? '(###) ###-####' : field.type === 'ssn' ? '###-##-####' : '')}
        disabled={!previewMode}
        onChange={(e) => {
          if (field.type === 'phone') e.target.value = formatPhoneValue(e.target.value);
          if (field.type === 'ssn') e.target.value = formatSsnValue(e.target.value);
        }}
      />
    );
  };

  if (loading) return <div className="d-flex justify-content-center p-5"><Spinner animation="border" /></div>;

  return (
    <div>
      <PageTitle title="Form Builder" description="Build onboarding forms" />
      <Navbar pageTitle="Onboarding Form Builder" />
      {showConditionalHelp && !previewMode && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080, maxWidth: 380 }}>
          <Alert variant="primary" className="shadow-sm mb-0">
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div className="small">
                <div className="fw-semibold mb-1">Conditional Rendering</div>
                Select a Yes/No or True/False source in the same scope. The target will render only when the source is Yes/True.
                {conditionalSelectionTarget && (
                  <div className="mt-2">
                    <Button size="sm" variant="light" onClick={clearConditionalSelection}>
                      Cancel conditional rendering
                    </Button>
                  </div>
                )}
              </div>
              <Button size="sm" variant="light" className="py-0 px-2" onClick={() => setShowConditionalHelp(false)}>x</Button>
            </div>
          </Alert>
        </div>
      )}
      
        <div className="bg-white shadow-sm custom-sticky-top">
        <Container className="py-1 d-flex justify-content-between align-items-center px-4">
          <div className="d-flex gap-2 align-items-center">
            <Button variant="light" size="sm" onClick={() => handleAttemptExit('/onboarding')}><ArrowLeft className="me-1" />Back</Button>
            <Badge bg={formData.status === 'published' ? 'success' : 'secondary'}>{formData.status}</Badge>
          </div>
          <div className="d-flex gap-2">
            <Button size="sm" variant="outline-secondary" onClick={togglePreviewMode}>{previewMode ? <EyeOff className="me-1" /> : <Eye className="me-1" />}</Button>
            <Button size="sm" variant="outline-primary" onClick={openDraftSaveModal} disabled={saving || !canEdit}><Save className="me-1" />Save Draft</Button>
            <Button size="sm" variant="primary" onClick={openPublishModal} disabled={saving || !canEdit}>Publish</Button>
          </div> 
          </Container>
          </div>
          <Container className="py-4">
{error && <Alert variant="danger">{error}</Alert>}
        <Row className="g-3">
          <Col lg={3}>
            <div className="custom-sticky-top" style={{ top: '80px' }}>
            <Card className="mb-3 shadow-sm border-0">
              <Card.Header className="fw-semibold px-4 pt-3 pb- border-0">Fields</Card.Header>
              <Card.Body className="px-4 pt-1 pb-4">
                <Row className="g-2">
                  {FIELD_TYPES.map((t) => {
                    const Icon = t.icon;
                    return (
                      <Col xs={4} key={t.id} className="fields-selector-button">
                        <Button
                          variant="light-lite"
                          className="w-100 p-2 d-flex flex-column align-items-center justify-content-center gap-1"
                          disabled={!canEdit || previewMode}
                          draggable={canEdit && !previewMode}
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'copy';
                            e.dataTransfer.setData(DND_FIELD_TYPE_MIME, t.id);
                            e.dataTransfer.setData('text/plain', t.id);
                            setDraggedPaletteType(t.id);
                          }}
                          onDragEnd={clearBuilderDnD}
                          onClick={() => addField(t.id)}
                          title={t.name}
                          style={{border: '1px dashed var(--gray-300)'}}
                        >
                          <Icon size={14} />
                          <span className="small">{t.name}</span>
                        </Button>
                      </Col>
                    );
                  })}
                </Row>
              </Card.Body>
            </Card>
            <Card className="shadow-sm border-0">
              <Card.Header className="fw-semibold px-4 pt-3 pb- border-0">Preformed Blocks</Card.Header>
              <Card.Body className="p-0">
                <Accordion
                  flush
                  activeKey={preformedAccordionKey}
                  onSelect={(eventKey) => {
                    if (eventKey == null) return;
                    setPreformedAccordionKey(eventKey);
                  }}
                >
                  {PREFORMED_FIELDS.map((group) => (
                    <Accordion.Item eventKey={group.section.toLowerCase()} key={group.section}>
                      <Accordion.Header className="h6"><span className="small fw-semibold">{group.section}</span></Accordion.Header>
                      <Accordion.Body className="pt-2 pb-3">
                        <div className="d-flex flex-column gap-1">
                          {group.items.map((item) => (
                            <Button
                              key={`${group.section}-${item.label}`}
                              variant="light"
                              className="text-start"
                              size="sm"
                              disabled={!canEdit || previewMode}
                              draggable={canEdit && !previewMode}
                              onDragStart={(e) => {
                                e.dataTransfer.effectAllowed = 'copy';
                                e.dataTransfer.setData(DND_PREFORMED_MIME, JSON.stringify(item));
                                e.dataTransfer.setData('text/plain', item.label || item.type);
                                setDraggedPreformedItem(item);
                              }}
                              onDragEnd={clearBuilderDnD}
                              onClick={() => addField(item.type, item)}
                            >
                              {item.label}
                            </Button>
                          ))}
                        </div>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                  <Accordion.Item eventKey="templates">
                    <Accordion.Header className="h6"><span className="small fw-semibold">Template Blocks</span></Accordion.Header>
                    <Accordion.Body className="pt-2 pb-3">
                      <div className="d-flex flex-column gap-1">
                        {TEMPLATE_BLOCKS.map((template) => (
                          <Button
                            key={template.id}
                            variant="light"
                            className="text-start"
                            size="sm"
                            disabled={!canEdit || previewMode}
                            draggable={canEdit && !previewMode}
                            onDragStart={(e) => {
                              e.dataTransfer.effectAllowed = 'copy';
                              e.dataTransfer.setData(DND_TEMPLATE_MIME, template.id);
                              e.dataTransfer.setData('text/plain', template.name || template.id);
                              setDraggedTemplateId(template.id);
                            }}
                            onDragEnd={clearBuilderDnD}
                            onClick={() => addTemplateBlock(template)}
                          >
                            {template.name}
                          </Button>
                        ))}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                  <Accordion.Item eventKey="custom-blocks">
                    <Accordion.Header className="h6"><span className="small fw-semibold">Custom Blocks</span></Accordion.Header>
                    <Accordion.Body className="pt-2 pb-3">
                      <div className="d-flex flex-column gap-1">
                        {customBlocks.length === 0 && (
                          <div className="small text-muted">Save fields or containers from the canvas to reuse them here.</div>
                        )}
                        {customBlocks.map((block) => (
                          <ButtonGroup key={block.id} className="d-flex align-items-center">
                            <Button
                              variant="light"
                              className="text-start flex-grow-1"
                              size="sm"
                              disabled={!canEdit || previewMode}
                              draggable={canEdit && !previewMode}
                              onDragStart={(e) => {
                                e.dataTransfer.effectAllowed = 'copy';
                                e.dataTransfer.setData(DND_CUSTOM_BLOCK_MIME, block.id);
                                e.dataTransfer.setData('text/plain', block.name || block.id);
                                setDraggedCustomBlockId(block.id);
                              }}
                              onDragEnd={clearBuilderDnD}
                              onClick={() => addCustomBlock(block)}
                            >
                              {block.name}
                            </Button>
                            <Button
                              variant="light"
                              size="sm"
                              className="px-1"
                              title="Delete custom block"
                              onClick={() => removeCustomBlock(block.id)}
                              disabled={!canEdit || previewMode}
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </ButtonGroup>
                     
                        ))}
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Card.Body>
            </Card></div>
          </Col>

          <Col lg={9}>
            <Card className="shadow-sm border-0">
              <Card.Header className="px-4 py-3 border-0 fw-semibold">Form Canvas</Card.Header>
              <Card.Body className="px-4 pt-0 pb-4 d-flex flex-column gap-3">
                {/*{conditionalSelectionTarget && !previewMode && (
                  <Alert variant="primary" className="py-2 mb-1">
                    Select a Yes/No or True/False source in the same scope (top-level or same multi-container block).
                    <Button size="sm" variant="light" className="ms-2" onClick={clearConditionalSelection}>Cancel</Button>
                  </Alert>
                )}*/}
                {fields.length === 0 && (
                  <div
                    className={`text-center text-muted py-5 px-3 border border-2 border-dashed rounded ${dragOverField?.index === fields.length ? 'border-primary bg-primary-subtle' : ''}`}
                    onDragOver={(e) => handleDragOver(e, fields.length)}
                    onDrop={(e) => handleDrop(e, fields.length)}
                    onDragEnd={clearBuilderDnD}
                  >
                    No fields yet. Click a field type or drag it here to start.
                  </div>
                )}
                {fields.map((field, idx) => (
                  (() => {
                    const isVisibleByCondition = !previewMode || evaluateConditionalRule(field.conditionalRule, conditionAnswers);
                    if (!isVisibleByCondition) return null;
                    const isUtilityOutlineField = !field.isContainer && ['spacer', 'divider'].includes(field.type);
                    return (
                  <div
                    key={field.id}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragEnd={clearBuilderDnD}
                    className={`rounded position-relative ${previewMode || isUtilityOutlineField ? 'bg-transparent' : 'bg-light p-1'} ${dragOverField?.index === idx && dragOverField.position === 'center' ? 'bg-primary-subtle border-primary' : ''}`}
               
                    style={{ borderTopWidth: dragOverField?.index === idx && dragOverField.position === 'top' ? 4 : undefined }}
                  >
                    {!previewMode && !isUtilityOutlineField && (
                      <div className="d-flex justify-content-between align-items-center mb-1 p-2">
                      <div className="d-flex align-items-center gap-2">
                        <span
                          draggable
                          onDragStart={() => setDraggedIndex(idx)}
                          onDragEnd={clearBuilderDnD}
                          className="cursor-grab"
                          title="Drag field"
                        >
                          <GripVertical size={14} />
                        </span>
                        <strong>{field.type === 'header' ? 'Header' : (getDisplayFieldLabel(field) || field.type)}</strong>{field.type === 'table' && <Table size={14} />}
                      </div>
                      {!previewMode && (
                        <div className="d-flex align-items-center gap-1">
                          {field.type === 'header' && (
                            <Form.Select
                              size="sm"
                              value={field.headingLevel || 'h2'}
                              className="w-auto"
                              onChange={(e) => updateTopLevelField(field.id, (f) => ({ ...f, headingLevel: e.target.value }))}
                            >
                              <option value="h1">H1</option>
                              <option value="h2">H2</option>
                              <option value="h3">H3</option>
                              <option value="h4">H4</option>
                              <option value="h5">H5</option>
                              <option value="h6">H6</option>
                            </Form.Select>
                          )}
                          <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => moveField(idx, -1)}><ArrowUp  className="lucide-icon-sm"/></Button>
                          <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => moveField(idx, 1)}><ArrowDown  className="lucide-icon-sm"/></Button>
                          <Button
                            size="sm"
                            variant={field.conditionalRule ? 'primary' : 'light-lite'}
                            className="p-1 d-flex align-items-center"
                            title="Conditional rendering"
                            onClick={() => beginConditionalSelection({ kind: 'top', fieldId: field.id })}
                          >
                            <Eye className="lucide-icon-sm"/>
                          </Button>
                          <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => openSaveCustomBlockModal(idx)} title="Save as custom block"><Save  className="lucide-icon-sm"/></Button>
                          <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => openEditor(idx)}><Settings  className="lucide-icon-sm"/></Button>
                          <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => duplicateField(idx)}><Copy  className="lucide-icon-sm"/></Button>
                          <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => removeField(idx)}><Trash2  className="lucide-icon-sm"/></Button>
                        </div>
                      )}
                      </div>
                    )}

                    {!field.isContainer ? (
                      <div className={previewMode ? '' : `bg-white p-2 rounded ${isUtilityOutlineField ? 'border border-dashed' : 'border'}`}>
                        {!previewMode && field.conditionalRule?.sourceFieldId && (
                          <div className="small text-primary mb-1">
                            Shown when "{getSourceFieldLabelById(field.conditionalRule.sourceFieldId)}" is Yes/True
                          </div>
                        )}
                        {!previewMode && isUtilityOutlineField && (
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2">
                              <span
                                draggable
                                onDragStart={() => setDraggedIndex(idx)}
                                onDragEnd={clearBuilderDnD}
                                className="cursor-grab d-inline-flex align-items-center text-muted"
                                title="Drag field"
                              >
                                <GripVertical size={14} />
                              </span>
                              <span className="small fw-semibold">
                                {field.type === 'spacer'
                                  ? `Spacer (${Number(field.spacerHeight || 24)}px)`
                                  : `Divider (${Number(field.dividerSpacing ?? 16)}px)`}
                              </span>
                            </div>
                            <div className="d-flex align-items-center gap-1">
                              <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => moveField(idx, -1)}><ArrowUp  className="lucide-icon-sm"/></Button>
                              <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => moveField(idx, 1)}><ArrowDown  className="lucide-icon-sm"/></Button>
                              <Button
                                size="sm"
                                variant={field.conditionalRule ? 'primary' : 'light-lite'}
                                className="p-1 d-flex align-items-center"
                                title="Conditional rendering"
                                onClick={() => beginConditionalSelection({ kind: 'top', fieldId: field.id })}
                              >
                                <Eye className="lucide-icon-sm"/>
                              </Button>
                              <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => openSaveCustomBlockModal(idx)} title="Save as custom block"><Save  className="lucide-icon-sm"/></Button>
                              <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => openEditor(idx)}><Settings  className="lucide-icon-sm"/></Button>
                              <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => duplicateField(idx)}><Copy  className="lucide-icon-sm"/></Button>
                              <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => removeField(idx)}><Trash2  className="lucide-icon-sm"/></Button>
                            </div>
                          </div>
                        )}
                        {!field.isContainer && !['header', 'textinfo', 'header_logo_content', 'yes_no', 'true_false', 'divider', 'spacer', 'table', 'dropdown', 'checkbox', 'signature'].includes(field.type) && (previewMode ? !['divider', 'spacer', 'table'].includes(field.type) : true) && <Form.Label className="small fw-semibold">{getDisplayFieldLabel(field)}{field.required ? ' *' : ''}</Form.Label>}
                        {renderField(field)}
                      </div>
                    ) : (
                      <div
                        className={`rounded position-relative ${
                          previewMode
                            ? ((field.backgroundColor && !['transparent', 'none'].includes(String(field.backgroundColor).toLowerCase())) || (Array.isArray(field.borderSides) && field.borderSides.length > 0))
                              ? 'p-3'
                              : ''
                            : `p-3 ${Array.isArray(field.borderSides) && field.borderSides.length > 0 ? 'border-0' : 'border border-dashed border-secondary-subtle'}`
                        }`}
                        style={(() => {
                          const s = getContainerStyle(field);
                          if (!previewMode) {
                            const transparentBg =
                              !field.backgroundColor ||
                              ['transparent', 'none'].includes(String(field.backgroundColor).toLowerCase());
                            if (transparentBg) return { ...s, backgroundColor: 'var(--bs-body-bg, #ffffff)' };
                          }
                          return s;
                        })()}
                        onDragOver={(e) => {
                          if (previewMode || draggedTemplateId) return;
                          if (!draggedNested && !draggedPaletteType && !draggedPreformedItem && !draggedCustomBlockId) return;
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = draggedNested ? 'move' : 'copy';
                        }}
                        onDrop={(e) => handleDropOnContainerBody(e, field)}
                      >
                        {(field.sectionTitle || field.sectionSubtitle || field.sectionDescription) && (
                          <>
                            {field.sectionTitle && <h6 className="mb-1 fw-semibold">{field.sectionTitle}</h6>}
                            {field.sectionSubtitle && <div className="text-muted fw-semibold mb-1">{field.sectionSubtitle}</div>}
                            {field.sectionDescription && <p className="text-muted small mb-2">{field.sectionDescription}</p>}
                          </>
                        )}

                        <div className="d-flex flex-column gap-2">
                          {getContainerRows(field).map((row, rowIdx) => {
                            if (previewMode && !evaluateConditionalRule(row.conditionalRule, conditionAnswers)) return null;
                            const rowHasBooleanChoice = (row.fields || []).some((nf) => ['yes_no', 'true_false'].includes(nf?.type));
                            return (
                            <div
                              key={row.id}
                              data-form-builder-row=""
                              className={`rounded  ${draggedNested && hoveredNested?.containerId === field.id ? 'bg-light' : ''}`}
                              onDragOver={(e) => {
                                if (draggedTemplateId) return;
                                if (!draggedNested && !draggedPaletteType && !draggedPreformedItem && !draggedCustomBlockId) return;
                                e.preventDefault();
                                e.stopPropagation();
                                e.dataTransfer.dropEffect = draggedNested ? 'move' : 'copy';
                              }}
                              onDrop={(e) => handleDropOnContainerRow(e, field.id, row.id)}
                            >
                              {!previewMode && (
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <div className="d-flex flex-column">
                                    <small className="text-muted fw-semibold">Row {rowIdx + 1}</small>
                                    {row.conditionalRule?.sourceFieldId && (
                                      <small className="text-primary">
                                        Shown when "{getSourceFieldLabelById(row.conditionalRule.sourceFieldId)}" is Yes/True
                                      </small>
                                    )}
                                  </div>
                                  <div className="d-flex gap-1">
                                    <Button
                                      size="sm"
                                      variant={row.conditionalRule ? 'primary' : 'light-lite'}
                                      className="d-flex align-items-center p-1"
                                      title="Conditional rendering"
                                      onClick={() => beginConditionalSelection({ kind: 'row', containerId: field.id, rowId: row.id })}
                                    >
                                      <Eye className="lucide-icon-sm" />
                                    </Button>
                                    {row.conditionalRule && (
                                      <Button
                                        size="sm"
                                        variant="light-lite"
                                        className="d-flex align-items-center p-1"
                                        title="Remove condition"
                                        onClick={() => clearConditionalRuleForTarget({ kind: 'row', containerId: field.id, rowId: row.id })}
                                      >
                                        <EyeOff className="lucide-icon-sm" />
                                      </Button>
                                    )}
                                    <Button size="sm" variant="light-lite" className="d-flex align-items-center p-1" onClick={() => addContainerRow(field.id)}>
                                      <Plus className="lucide-icon-sm" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="light-lite"
                                      className="d-flex align-items-center p-1"
                                      onClick={() => removeContainerRow(field.id, row.id)}
                                      disabled={getContainerRows(field).length <= 1}
                                    >
                                      <Trash2 className="lucide-icon-sm" />
                                    </Button>
                                  </div>
                             
                                </div>
                              )}
                              <div className={`d-flex gap-2 ${rowHasBooleanChoice ? 'align-items-end' : ''}`}>
                                {(row.fields || []).map((nf) => (
                                  (previewMode && !evaluateConditionalRule(nf.conditionalRule, conditionAnswers)) ? null : (
                                  <div
                                    key={nf.id}
                                    style={{ flex: nf.flexBasis || 1 }}
                                    className={`border border-dashed rounded ${previewMode ? 'p-0 bg-transparent border-0' : 'p-2 bg-white'} position-relative`}
                                    onMouseEnter={() => setHoveredNested({ containerId: field.id, nestedId: nf.id })}
                                    onMouseLeave={() => setHoveredNested(null)}
                                  >
                              {!previewMode && (
                                <div
                                  className="position-absolute top-0 end-0 p-1 d-flex"
                                  style={{ opacity: hoveredNested?.containerId === field.id && hoveredNested?.nestedId === nf.id ? 1 : 0, pointerEvents: hoveredNested?.containerId === field.id && hoveredNested?.nestedId === nf.id ? 'auto' : 'none' }}
                                >
                                  {nf.type === 'header' && (
                                    <Dropdown onSelect={(val) => updateNestedFieldValue(field.id, row.id, nf.id, (n) => ({ ...n, headingLevel: val }))}> 
                                      <Dropdown.Toggle variant="light-lite" size="sm" className="p-1 d-flex align-items-center no-arrow" title={nf.headingLevel ? nf.headingLevel.toUpperCase() : "H2"}>
                                        <Heading className="lucide-icon-sm" />
                                      </Dropdown.Toggle>
                                      <Dropdown.Menu>
                                        <Dropdown.Item eventKey="h1" active={nf.headingLevel === 'h1'}>H1</Dropdown.Item>
                                        <Dropdown.Item eventKey="h2" active={nf.headingLevel === 'h2'}>H2</Dropdown.Item>
                                        <Dropdown.Item eventKey="h3" active={nf.headingLevel === 'h3'}>H3</Dropdown.Item>
                                        <Dropdown.Item eventKey="h4" active={nf.headingLevel === 'h4'}>H4</Dropdown.Item>
                                        <Dropdown.Item eventKey="h5" active={nf.headingLevel === 'h5'}>H5</Dropdown.Item>
                                        <Dropdown.Item eventKey="h6" active={nf.headingLevel === 'h6'}>H6</Dropdown.Item>
                                      </Dropdown.Menu>
                                    </Dropdown>
                                  )}
                                  <Button
                                    size="sm"
                                    variant={nf.conditionalRule ? 'primary' : 'light-lite'}
                                    className="p-1 d-flex align-items-center"
                                    title="Conditional rendering"
                                    onClick={() => beginConditionalSelection({ kind: 'nested', containerId: field.id, rowId: row.id, nestedId: nf.id })}
                                  >
                                    <Eye className="lucide-icon-sm"/>
                                  </Button>
                             
                                  <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => openEditor(idx, nf.id, row.id)}><Settings  className="lucide-icon-sm"/></Button>
                                  <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => {
                                    const copy = deepClone(nf);
                                    copy.id = uid('field');
                                    setFields(fields.map((f, i) => (
                                      i !== idx
                                        ? f
                                        : {
                                          ...f,
                                          containerRows: getContainerRows(f).map((rr) => (rr.id === row.id ? { ...rr, fields: [...(rr.fields || []), copy] } : rr))
                                        }
                                    )));
                                  }}><Copy  className="lucide-icon-sm"/></Button>
                                  <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => removeNestedField(field.id, row.id, nf.id)}>
                                       <Trash2 className="lucide-icon-sm" />
                                     </Button>
                                </div>
                              )}
                              {!previewMode && (
                                <>
                                  <div
                                    className="position-absolute start-0 top-50 translate-middle-y "
                                    style={{ marginLeft: '-14px', zIndex: 1000, opacity: hoveredNested?.containerId === field.id && hoveredNested?.nestedId === nf.id ? 1 : 0, pointerEvents: hoveredNested?.containerId === field.id && hoveredNested?.nestedId === nf.id ? 'auto' : 'none' }}
                                  >
                                   <ButtonGroup vertical className="bg-white border shadow-sm rounded-pill overflow-hidden">
                                     <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => moveNestedField(field.id, nf.id, 'left')}>
                                       <ArrowLeft className="lucide-icon-sm" />
                                     </Button>
                                     <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => adjustNestedFieldWidth(field.id, nf.id, false)}>
                                       <Minus className="lucide-icon-sm" />
                                     </Button>
                                   </ButtonGroup>
                               
                                  </div>
                                  <div
                                    className="position-absolute end-0 top-50 translate-middle-y"
                                    style={{ marginRight: '-14px', zIndex: 1000, opacity: hoveredNested?.containerId === field.id && hoveredNested?.nestedId === nf.id ? 1 : 0, pointerEvents: hoveredNested?.containerId === field.id && hoveredNested?.nestedId === nf.id ? 'auto' : 'none' }}
                                  >
                                    <ButtonGroup vertical className="bg-white border shadow-sm rounded-pill overflow-hidden">
                                      <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" onClick={() => moveNestedField(field.id, nf.id, 'right')}>
                                        <ArrowRight className="lucide-icon-sm" />
                                      </Button>
                                      <Button size="sm" variant="light-lite" className="p-1 d-flex align-items-center" style={{ width: 26, height: 26 }} onClick={() => adjustNestedFieldWidth(field.id, nf.id, true)}>
                                        <Plus className="lucide-icon-sm" />
                                      </Button>
                                    </ButtonGroup>
                               
                                  </div>
                                </>
                              )}
                              {!previewMode && ['header', 'textinfo', 'header_logo_content', 'yes_no', 'true_false'].includes(nf.type) && (
                                <div className="mb-1 d-flex align-items-center gap-1">
                                  <span
                                    draggable
                                    onDragStart={() => setDraggedNested({ containerId: field.id, rowId: row.id, field: nf })}
                                    onDragEnd={clearBuilderDnD}
                                    className="d-inline-flex align-items-center cursor-grab text-muted"
                                    title="Drag to reorder row, another row, or onto canvas"
                                  >
                                    <GripVertical size={12} />
                                  </span>
                                  {nf.type === 'header' && <span className="small fw-semibold">Header</span>}
                                  {nf.type === 'header_logo_content' && <span className="small fw-semibold">Logo + Header</span>}
                                  {(nf.type === 'yes_no' || nf.type === 'true_false') && isVerticalBooleanField(nf) && (
                                    <span className="small fw-semibold mb-1">{getDisplayFieldLabel(nf)}{nf.required ? ' *' : ''}</span>
                                  )}
                                </div>
                              )}
                              {!previewMode && ['dropdown', 'checkbox', 'signature'].includes(nf.type) && (
                                <Form.Label className="small fw-semibold d-flex align-items-center gap-1 mb-1">
                                  <span
                                    draggable
                                    onDragStart={() => setDraggedNested({ containerId: field.id, rowId: row.id, field: nf })}
                                    onDragEnd={clearBuilderDnD}
                                    className="d-inline-flex align-items-center cursor-grab"
                                    title="Drag to reorder row, another row, or onto canvas"
                                  >
                                    <GripVertical size={12} />
                                  </span>
                                  <span>{getDisplayFieldLabel(nf)}{nf.required ? ' *' : ''}</span>
                                </Form.Label>
                              )}
                              {!previewMode && nf.conditionalRule?.sourceFieldId && (
                                <div className="small text-primary mb-1">
                                  Shown when "{getSourceFieldLabelById(nf.conditionalRule.sourceFieldId)}" is Yes/True
                                </div>
                              )}
                              {!['header', 'textinfo', 'header_logo_content', 'yes_no', 'true_false', 'dropdown', 'checkbox', 'signature'].includes(nf.type) && (previewMode ? !['divider', 'spacer'].includes(nf.type) : true) && (
                                <Form.Label className="small fw-semibold d-flex align-items-center gap-1">
                                  {!previewMode && nf.type !== 'header' && (
                                    <span
                                      draggable
                                      onDragStart={() => setDraggedNested({ containerId: field.id, rowId: row.id, field: nf })}
                                      onDragEnd={clearBuilderDnD}
                                      className="d-inline-flex align-items-center cursor-grab"
                                      title="Drag to reorder row, another row, or onto canvas"
                                    >
                                      <GripVertical size={12} />
                                    </span>
                                  )}
                                  <span>{nf.type === 'header' ? 'Header' : getDisplayFieldLabel(nf)}{nf.type !== 'header' && nf.required ? ' *' : ''}</span>
                                </Form.Label>
                              )}
                              {renderField({ ...nf, __nestedCtx: { containerId: field.id, rowId: row.id } }, field)}
                                  </div>
                                  )
                                ))}
                              </div>
                            </div>
                          );
                          })}
                          {field.allowMultipleEntries && !field.isHeaderBlock && (
                            <div className="pt-2">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="w-100"
                                onClick={() => addContainerEntry(idx)}
                                disabled
                                title="Available to residents during form completion"
                              >
                                <Plus className="lucide-icon-sm me-1" />
                                Add another entry
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {!previewMode && dragOverField?.index === idx && dragOverField.position === 'center' && !draggedNested && !draggedPaletteType && !draggedPreformedItem && !draggedTemplateId && !draggedCustomBlockId && (
                      <div className="position-absolute top-50 start-50 translate-middle">
                        <Badge bg="primary">Drop to Merge</Badge>
                      </div>
                    )}
                  </div>
                    );
                  })()
                ))}
                {fields.length > 0 && !previewMode && (
                  <div
                    className={`py-3 px-2 border border-dashed rounded text-center small text-muted ${dragOverField?.index === fields.length ? 'border-primary bg-primary-subtle' : 'border-secondary-subtle'}`}
                    onDragOver={(e) => handleDragOver(e, fields.length)}
                    onDrop={(e) => handleDrop(e, fields.length)}
                    onDragEnd={clearBuilderDnD}
                  >
                    Drop here to place at end of form
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={showFieldModal} onHide={() => setShowFieldModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit {fieldDraft?.isContainer ? 'Container' : 'Field'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {fieldDraft && (
            <div className="d-flex flex-column gap-3">
              <Form.Floating>
                <Form.Control
                  id="floating-label"
                  value={fieldDraft.label || ''}
                  placeholder={fieldDraft.type === 'header' ? 'Title' : 'Label'}
                  onChange={(e) => {
                    const nextLabel = e.target.value;
                    setFieldDraft((p) => (
                      !p || p.isContainer || !['dropdown', 'checkbox'].includes(p.type)
                        ? { ...p, label: nextLabel }
                        : { ...p, label: nextLabel, choiceTitle: nextLabel }
                    ));
                  }}
                />
                <label htmlFor="floating-label">{fieldDraft.type === 'header' ? 'Title' : 'Label'}</label>
              </Form.Floating>
              {fieldDraft.conditionalRule?.sourceFieldId && (
                <Alert variant="light" className="py-2 mb-1">
                  <div className="d-flex justify-content-between align-items-center gap-2">
                    <small>
                      Conditioned on <strong>{getSourceFieldLabelById(fieldDraft.conditionalRule.sourceFieldId)}</strong> being Yes/True.
                    </small>
                    <Button size="sm" variant="outline-danger" onClick={() => setFieldDraft((p) => ({ ...p, conditionalRule: null }))}>
                      Remove
                    </Button>
                  </div>
                </Alert>
              )}

              {!fieldDraft.isContainer && (fieldDraft.type === 'yes_no' || fieldDraft.type === 'true_false') && (
                <Form.Group className="d-flex flex-row align-items-center justify-content-start gap-2 bg-light rounded-3">
                  <Form.Label className="d-flex align-items-center mb-0 small fw-semibold ps-3">Display</Form.Label>
                  <div className="radio-inputs flex-wrap" style={{ width: 'fit-content' }}>
                    {[
                      { value: 'horizontal', label: 'Row', icon: BetweenHorizontalStart },
                      { value: 'vertical', label: 'Column', icon: BetweenVerticalStart }
                    ].map((option) => (
                      <label key={`boolean-layout-${option.value}`} className="radio">
                        <input
                          type="radio"
                          name="field-boolean-layout-radio"
                          checked={(fieldDraft.booleanLayout ?? 'horizontal') === option.value}
                          onChange={() => setFieldDraft((p) => ({ ...p, booleanLayout: option.value }))}
                          aria-label={option.label}
                        />
                        <span className="name d-inline-flex align-items-center gap-1 px-3">
                          <option.icon size={14} />
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </Form.Group>
              )}

              {fieldDraft.isContainer && (
                <>
                  <Form.Floating>
                    <Form.Control
                      id="floating-section-title"
                      value={fieldDraft.sectionTitle || ''}
                      placeholder="Section Title"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, sectionTitle: e.target.value }))}
                    />
                    <label htmlFor="floating-section-title">Section Title</label>
                  </Form.Floating>
                  <Form.Floating>
                    <Form.Control
                      id="floating-section-subtitle"
                      value={fieldDraft.sectionSubtitle || ''}
                      placeholder="Section Subtitle"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, sectionSubtitle: e.target.value }))}
                    />
                    <label htmlFor="floating-section-subtitle">Section Subtitle</label>
                  </Form.Floating>
                  <Form.Floating className="mb-2">
                    <Form.Control
                      as="textarea"
                      style={{ height: '60px' }}
                      id="floating-section-description"
                      rows={2}
                      value={fieldDraft.sectionDescription || ''}
                      placeholder="Section Description"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, sectionDescription: e.target.value }))}
                    />
                    <label htmlFor="floating-section-description">Section Description</label>
                  </Form.Floating>
                  <Row>
                    <Col md={6}>
                  <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 border rounded-3 p-2">
                    <Form.Label htmlFor="floating-bg-color" className="d-block mb-0 small fw-semibold">Background Color</Form.Label>
                    <Form.Control
                      type="color"
                      id="floating-bg-color"
                      value={fieldDraft.backgroundColor || '#f8f9fa'}
                      placeholder="Background Color"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, backgroundColor: e.target.value }))}
                      style={{ height: 24}}
                      className="border-0 p-0"
                    />
                  </Form.Group>
                  </Col>
                  <Col md={6}>
                  <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 border rounded-3 p-2">
                    <Form.Label htmlFor="floating-border-color" className="d-block mb-0 small fw-semibold">Border Color</Form.Label>
                    <Form.Control
                      type="color"
                      id="floating-border-color"
                      value={fieldDraft.borderColor || '#dee2e6'}
                      placeholder="Border Color"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, borderColor: e.target.value }))}
                      style={{ height: 24}}
                      className="border-0 p-0"
                    />
                  </Form.Group>
                  </Col>
                  </Row>
           
                  {!fieldDraft.isHeaderBlock && (
                    <Form.Check
                      type="switch"
                      id="allow-multiple-entries-switch"
                      label="Allow multiple entries"
                      checked={Boolean(fieldDraft.allowMultipleEntries)}
                      onChange={(e) => setFieldDraft((p) => ({ ...p, allowMultipleEntries: e.target.checked }))}
                    />
                  )}
                  <div className="d-flex flex-row align-items-center justify-content-start gap-2 bg-light rounded-3">
                    <Form.Label className="d-flex align-items-center mb-0 small fw-semibold ps-3">Border</Form.Label>
                    <div className="radio-inputs flex-wrap" style={{ width: 'fit-content' }}>
                      {['none', 'all', 'top', 'right', 'bottom', 'left'].map((side) => (
                        <label key={side} className="radio">
                          <input
                          type="radio"
                          name="field-border-sides-radio"
                          checked={side === 'none'
                            ? !Array.isArray(fieldDraft.borderSides) || fieldDraft.borderSides.length === 0
                            : (fieldDraft.borderSides || []).includes(side)}
                          onChange={() => setFieldDraft((p) => ({ ...p, borderSides: side === 'none' ? [] : [side] }))}
                          aria-label={side}
                        />
                          <span className="name text-capitalize d-inline-flex align-items-center gap-1 px-2">
                            {side === 'none' ? (
                              <SquareDashed size={14} style={{ opacity: 0.6 }} />
                            ) : side === 'all' ? (
                              <Square size={14} />
                            ) : (
                              <SquareDashedTopSolid
                                size={14}
                                style={{
                                  transform: `rotate(${{
                                    top: 0,
                                    right: 90,
                                    bottom: 180,
                                    left: 270,
                                  }[side]}deg)`,
                                }}
                              />
                            )}
                            
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {fieldDraft.isHeaderLogoContent && (
                <>
                  <Form.Floating>
                    <Form.Control
                      id="floating-logo-url"
                      value={fieldDraft.logo || ''}
                      placeholder="Logo URL"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, logo: e.target.value }))}
                    />
                    <label htmlFor="floating-logo-url">Logo URL</label>
                  </Form.Floating>
                  <Form.Floating>
                    <Form.Control
                      id="floating-header-title"
                      value={fieldDraft.headerTitle || ''}
                      placeholder="Header Title"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, headerTitle: e.target.value }))}
                    />
                    <label htmlFor="floating-header-title">Header Title</label>
                  </Form.Floating>
                  <Form.Floating>
                    <Form.Control
                      id="floating-header-subtitle"
                      value={fieldDraft.headerSubtitle || ''}
                      placeholder="Header Subtitle"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, headerSubtitle: e.target.value }))}
                    />
                    <label htmlFor="floating-header-subtitle">Header Subtitle</label>
                  </Form.Floating>
                  <Form.Group className="d-flex flex-row align-items-center justify-content-start gap-2 bg-light rounded-3">
                    <Form.Label className=" d-flex align-items-center mb-0 small fw-semibold ps-3">Alignment</Form.Label>
                    <div className="radio-inputs flex-wrap" style={{ width: 'fit-content' }}>
                      {[
                        { value: 'left', label: 'Left', icon: AlignStartVertical },
                        { value: 'center', label: 'Center', icon: AlignHorizontalJustifyCenter },
                        { value: 'right', label: 'Right', icon: AlignEndVertical }
                      ].map((option) => (
                        <label key={`alignment-${option.label}`} className="radio">
                          <input
                            type="radio"
                            name="field-alignment-radio"
                            checked={(fieldDraft.alignment ?? 'left') === option.value}
                            onChange={() => setFieldDraft((p) => ({ ...p, alignment: option.value }))}
                            aria-label={option.label}
                          />
                          <span className="name d-inline-flex align-items-center gap-1 px-3">
                            <option.icon size={14} />
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </Form.Group>
                </>
              )}

              {!fieldDraft.isContainer && fieldDraft.type === 'textinfo' &&
                <Form.Group>
                  <Form.Label>Text Content</Form.Label>
                  <RichTextEditor value={fieldDraft.textContent || ''} onChange={(value) => setFieldDraft((p) => ({ ...p, textContent: value }))} placeholder="Add informational text..." />
                </Form.Group>
              }
              {!fieldDraft.isContainer && fieldDraft.type === 'table' && (
                <>
                  <Form.Floating>
                    <Form.Control
                      id="floating-table-title"
                      value={fieldDraft.tableTitle || ''}
                      placeholder="Title"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, tableTitle: e.target.value }))}
                    />
                    <label htmlFor="floating-table-title">Title</label>
                  </Form.Floating>
                  <Form.Floating>
                    <Form.Control
                      id="floating-table-subtitle"
                      value={fieldDraft.tableSubtitle || ''}
                      placeholder="Subtitle"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, tableSubtitle: e.target.value }))}
                    />
                    <label htmlFor="floating-table-subtitle">Subtitle</label>
                  </Form.Floating>
                  <Form.Floating className="mb-2">
                    <Form.Control
                      as="textarea"
                      id="floating-table-description"
                      rows={3}
                      value={fieldDraft.tableDescription || ''}
                      placeholder="Description"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, tableDescription: e.target.value }))}
                    />
                    <label htmlFor="floating-table-description">Description</label>
                  </Form.Floating>
                </>
              )}

              {!fieldDraft.isContainer && fieldDraft.type === 'header' && (
                <>     
                <Form.Floating>
                    <Form.Control
                      id="floating-header-subtitle2"
                      value={fieldDraft.headerSubtitle || ''}
                      placeholder="Subtitle"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, headerSubtitle: e.target.value }))}
                    />
                    <label htmlFor="floating-header-subtitle2">Subtitle</label>
                  </Form.Floating>
                  <Form.Floating className="mb-2">
                    <Form.Control
                      as="textarea"
                      id="floating-header-description"
                      rows={3}
                      value={fieldDraft.headerDescription || ''}
                      placeholder="Description"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, headerDescription: e.target.value }))}
                    />
                    <label htmlFor="floating-header-description">Description</label>
                  </Form.Floating>
                <Row>
                  <Col md={9} sm={9} xs={8}>
                  <Form.Group className="d-flex flex-row align-items-center justify-content-between gap-2 bg-light rounded-3">
                    <Form.Label className="d-flex align-items-center mb-0 small fw-semibold ps-3">Alignment</Form.Label>
                    <div className="radio-inputs flex-wrap" style={{ width: 'fit-content' }}>
                      {[
                        { value: 'left', label: 'Left', icon: AlignStartVertical },
                        { value: 'center', label: 'Center', icon: AlignHorizontalJustifyCenter },
                        { value: 'right', label: 'Right', icon: AlignEndVertical }
                      ].map((option) => (
                        <label key={`header-alignment-${option.label}`} className="radio">
                          <input
                            type="radio"
                            name="field-header-alignment-radio"
                            checked={(fieldDraft.headerAlignment ?? 'left') === option.value}
                            onChange={() => setFieldDraft((p) => ({ ...p, headerAlignment: option.value }))}
                            aria-label={option.label}
                          />
                          <span className="name d-inline-flex align-items-center gap-1 px-2">
                            <option.icon size={14} />
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </Form.Group>
                  </Col>
                  <Col md={3} sm={3} xs={4}>
                  <Form.Group>
                    <Form.Select value={fieldDraft.headingLevel || 'h2'} onChange={(e) => setFieldDraft((p) => ({ ...p, headingLevel: e.target.value }))}>
                      <option value="h1">H1</option>
                      <option value="h2">H2</option>
                      <option value="h3">H3</option>
                      <option value="h4">H4</option>
                      <option value="h5">H5</option>
                      <option value="h6">H6</option>
                    </Form.Select>
                  </Form.Group>  
                  </Col>
                  </Row>
            
             
                </>
              )}
              {!fieldDraft.isContainer && fieldDraft.type === 'divider' && (
                <>
                  <Form.Group>
                    <Form.Label>Line Style</Form.Label>
                    <div className="radio-inputs flex-wrap" style={{ maxWidth: '360px' }}>
                      {[
                        { value: 'none', label: 'None' },
                        { value: 'solid', label: 'Solid' },
                        { value: 'dashed', label: 'Dashed' },
                        { value: 'dotted', label: 'Dotted' }
                      ].map((option) => (
                        <label key={`line-style-${option.value}`} className="radio">
                          <input
                            type="radio"
                            name="field-divider-style-radio"
                            checked={(fieldDraft.dividerStyle ?? 'solid') === option.value}
                            onChange={() => setFieldDraft((p) => ({ ...p, dividerStyle: option.value }))}
                            aria-label={option.label}
                          />
                          <span className="name">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </Form.Group>
                  <Form.Floating>
                    <Form.Control
                      type="number"
                      min={0}
                      id="floating-divider-spacing"
                      value={fieldDraft.dividerSpacing ?? 16}
                      placeholder="Spacing (px)"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, dividerSpacing: Number(e.target.value || 0) }))}
                    />
                    <label htmlFor="floating-divider-spacing">Spacing (px)</label>
                  </Form.Floating>
                  <Form.Floating>
                    <Form.Control
                      type="number"
                      min={1}
                      id="floating-divider-thickness"
                      value={fieldDraft.dividerThickness ?? 1}
                      placeholder="Thickness (px)"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, dividerThickness: Number(e.target.value || 1) }))}
                    />
                    <label htmlFor="floating-divider-thickness">Thickness (px)</label>
                  </Form.Floating>
                  <Form.Floating>
                    <Form.Control
                      type="color"
                      id="floating-divider-color"
                      value={fieldDraft.dividerColor && fieldDraft.dividerColor.startsWith('#') ? fieldDraft.dividerColor : '#9ca3af'}
                      placeholder="Color"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, dividerColor: e.target.value }))}
                      style={{ height: 38 }}
                    />
                    <label htmlFor="floating-divider-color" className="d-block">Color</label>
                  </Form.Floating>
                </>
              )}
              {!fieldDraft.isContainer && fieldDraft.type === 'spacer' && (
                <Form.Floating>
                  <Form.Control
                    type="number"
                    min={0}
                    id="floating-spacer-height"
                    value={fieldDraft.spacerHeight ?? 24}
                    placeholder="Spacer Height (px)"
                    onChange={(e) => setFieldDraft((p) => ({ ...p, spacerHeight: Number(e.target.value || 0) }))}
                  />
                  <label htmlFor="floating-spacer-height">Spacer Height (px)</label>
                </Form.Floating>
              )}
              {!fieldDraft.isContainer &&
                ![
                  'header',
                  'textinfo',
                  'dropdown',
                  'checkbox',
                  'yes_no',
                  'true_false',
                  'signature',
                  'table',
                  'header_logo_content',
                  'divider',
                  'spacer'
                ].includes(fieldDraft.type) && (
                  <Form.Floating>
                    <Form.Control
                      id="floating-placeholder"
                      value={fieldDraft.placeholder || ''}
                      placeholder="Placeholder"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, placeholder: e.target.value }))}
                    />
                    <label htmlFor="floating-placeholder">Placeholder</label>
                  </Form.Floating>
                )
              }

              {!fieldDraft.isContainer && (fieldDraft.type === 'dropdown' || fieldDraft.type === 'checkbox') && (
                <>
                  <Form.Floating className="mb-2">
                    <Form.Control
                      as="textarea"
                      id="floating-choice-description"
                      rows={2}
                      value={fieldDraft.choiceDescription || ''}
                      placeholder="Description (optional)"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, choiceDescription: e.target.value }))}
                    />
                    <label htmlFor="floating-choice-description">description</label>
                  </Form.Floating>
                </>
              )}
              {!fieldDraft.isContainer && fieldDraft.type === 'signature' && (
                <>
                  <Form.Group className="d-flex flex-row align-items-center justify-content-start gap-2 bg-light rounded-3">
                    <Form.Label className="d-flex align-items-center mb-0 small fw-semibold ps-3">Name style</Form.Label>
                    <div className="radio-inputs flex-wrap" style={{ width: 'fit-content' }}>
                      {[
                        { value: 'typed', label: 'Typed' },
                        { value: 'cursive', label: 'Cursive' }
                      ].map((option) => (
                        <label key={`name-style-${option.value}`} className="radio">
                          <input
                            type="radio"
                            name="field-signature-name-style-radio"
                            checked={(fieldDraft.nameDisplayStyle ?? 'cursive') === option.value}
                            onChange={() => setFieldDraft((p) => ({ ...p, nameDisplayStyle: option.value }))}
                            aria-label={option.label}
                          />
                          <span className="name px-3">{option.label}</span>
                        </label>
                      ))}
                    </div>
                  </Form.Group>
                  <Form.Floating>
                    <Form.Control
                      id="floating-signature-name-label"
                      value={fieldDraft.nameLabel || ''}
                      placeholder="Name field label"
                      onChange={(e) => setFieldDraft((p) => ({ ...p, nameLabel: e.target.value }))}
                    />
                    <label htmlFor="floating-signature-name-label">Name field label</label>
                  </Form.Floating>
                  <Form.Group className="mt-1">
                    <Form.Label className="small fw-semibold">Agreement items</Form.Label>
                    <p className="text-muted small mb-2">Residents must check each required item before signing.</p>
                    {(fieldDraft.consentItems || []).map((item, i) => {
                      const items = fieldDraft.consentItems || [];
                      const isLast = i === items.length - 1;
                      return (
                        <Row key={item.id || `consent_${i}`} className="mb-1">
                          <Col xs={12}>
                            <div className="d-flex flex-row align-items-start gap-2 p-1 bg-light rounded-3">
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={item.text || ''}
                                onChange={(e) =>
                                  setFieldDraft((p) => ({
                                    ...p,
                                    consentItems: (p.consentItems || []).map((c, idx) =>
                                      idx === i ? { ...c, text: e.target.value } : c
                                    )
                                  }))
                                }
                                placeholder={`Agreement ${i + 1}`}
                              />
                              <Button
                                type="button"
                                variant="outline-danger"
                                size="sm"
                                className=""
                                disabled={items.length <= 1}
                                onClick={() =>
                                  setFieldDraft((p) => {
                                    const next = [...(p.consentItems || [])];
                                    if (next.length <= 1) return p;
                                    next.splice(i, 1);
                                    return { ...p, consentItems: next };
                                  })
                                }
                              >
                                <X className="lucide-icon-sm" />
                              </Button>
                              {isLast && (
                                <Button
                                  type="button"
                                  variant="outline-primary"
                                  size="sm"
                                  className="flex-shrink-0"
                                  onClick={() =>
                                    setFieldDraft((p) => ({
                                      ...p,
                                      consentItems: [
                                        ...(p.consentItems || []),
                                        { id: uid('consent'), text: '', required: true }
                                      ]
                                    }))
                                  }
                                >
                                  <Plus className="lucide-icon-sm" />
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      );
                    })}
                  </Form.Group>
                </>
              )}
              {!fieldDraft.isContainer && (fieldDraft.type === 'dropdown' || fieldDraft.type === 'checkbox') && (
                <Form.Group className="mt-1">
                  <Form.Label className="small fw-semibold">Options</Form.Label>
                  <p className="text-muted small mb-2">Drag the handle to reorder. Add or remove options below.</p>
                  <div ref={fieldDraftOptionListRef}>
                    {(fieldDraft.options || []).map((opt, i) => {
                      const options = fieldDraft.options || [];
                      const isLastOption = i === options.length - 1;
                      return (
                        <Row key={opt.id || `opt_${fieldDraft.id}_${i}`} className="field-draft-option-row">
                          <Col xs={12}>
                            <div className="d-flex flex-row align-items-center mb-1 p-1 bg-light rounded-3 position-relative">
                              <span
                                className="field-option-drag-handle d-inline-flex align-items-center justify-content-center px-1 text-muted"
                                style={{ cursor: 'grab' }}
                                title="Drag to reorder"
                              >
                                <GripVertical size={16} />
                              </span>
                              <div className="flex-grow-1 position-relative">
                                <Form.Control
                                  value={opt.label ?? ''}
                                  onChange={(e) => {
                                    const label = e.target.value;
                                    setFieldDraft((p) => ({
                                      ...p,
                                      options: (p.options || []).map((o, idx) => (idx === i ? { ...o, label } : o)),
                                    }));
                                  }}
                                  placeholder={`Option ${i + 1}`}
                                  style={{ paddingRight: isLastOption ? '120px' : '40px' }}
                                />
                                <Button
                                  type="button"
                                  variant="link-dng"
                                  className="position-absolute top-50 translate-middle-y p-0 px-1"
                                  style={{ right: '6px' }}
                                  size="sm"
                                  title="Remove option"
                                  disabled={options.length <= 1}
                                  onClick={() =>
                                    setFieldDraft((p) => {
                                      const o = [...(p.options || [])];
                                      if (o.length <= 1) return p;
                                      o.splice(i, 1);
                                      return { ...p, options: o };
                                    })
                                  }
                                >
                                  <X className="lucide-icon-sm" />
                                </Button>
                              </div>
                              {isLastOption && (
                                <Button
                                  type="button"
                                  variant="outline-primary"
                                  size="sm"
                                  className="flex-shrink-0 ms-2"
                                  onClick={() =>
                                    setFieldDraft((p) => {
                                      const cur = p.options || [];
                                      return {
                                        ...p,
                                        options: [
                                          ...cur,
                                          { id: uid('opt'), value: `option_${cur.length + 1}`, label: '' },
                                        ],
                                      };
                                    })
                                  }
                                >
                                  <Plus className="me-md-1 lucide-icon-sm" />
                                  <span className="d-none d-md-inline">Add option</span>
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      );
                    })}
                  </div>
                </Form.Group>
              )}

              {!fieldDraft.isContainer &&
                !['header', 'textinfo', 'table', 'header_logo_content', 'divider', 'spacer'].includes(fieldDraft.type) && (
                  <Form.Check
                    type="checkbox"
                    label="Required"
                    checked={Boolean(fieldDraft.required)}
                    onChange={(e) => setFieldDraft((p) => ({ ...p, required: e.target.checked }))}
                  />
                )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowFieldModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEditor}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSaveCustomBlockModal}
        onHide={() => {
          setShowSaveCustomBlockModal(false);
          setPendingCustomFieldIdx(null);
          setCustomBlockName('');
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Save Custom Block</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Block Name</Form.Label>
            <Form.Control
              value={customBlockName}
              onChange={(e) => setCustomBlockName(e.target.value)}
              placeholder="Enter a custom block name"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowSaveCustomBlockModal(false);
              setPendingCustomFieldIdx(null);
              setCustomBlockName('');
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmSaveCustomBlock}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDraftModal} onHide={() => setShowDraftModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Save Draft</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column gap-3">
            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control value={draftMeta.title} onChange={(e) => setDraftMeta((prev) => ({ ...prev, title: e.target.value }))} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" rows={3} value={draftMeta.description} onChange={(e) => setDraftMeta((prev) => ({ ...prev, description: e.target.value }))} />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDraftModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleConfirmDraftSave} disabled={saving}>Save Draft</Button>
          <Button variant="outline-primary" onClick={() => handleConfirmDraftSaveWithExit(true)} disabled={saving}>Save &amp; Exit</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPublishModal} onHide={() => setShowPublishModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Publish Form</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Publishing makes this form available for assignment and use in onboarding workflows.</p>
          <p className="text-muted mb-0">You can still return later and update the form.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPublishModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={() => handlePublish(false)} disabled={saving}>Publish</Button>
          <Button variant="outline-primary" onClick={() => handlePublish(true)} disabled={saving}>Publish &amp; Exit</Button>
        </Modal.Footer>
      </Modal>

      <NavigationWarningModal
        show={showExitWarningModal}
        onHide={handleCancelExit}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
        onSaveAndLeave={() => {
          setShowExitWarningModal(false);
          setShowDraftModal(true);
        }}
        isTimed={false}
        showSaveButton
        saveButtonLabel="Save Draft"
        message="You have unsaved changes in this form. Navigating away will lose your updates."
      />
    </div>
  );
};

export default OnboardingFormBuilderAdvancedPage;

